import React, { useState, useEffect, useRef } from 'react';
import { triggerHaptic } from './utils/haptics';
import { auth } from './firebase';
import { onAuthStateChanged, signOut, sendEmailVerification } from 'firebase/auth';
import { backgroundSync } from './services/backgroundSyncService';
import { PaymentProcessor } from './utils/PaymentProcessor';
import {
  AthleteProfile,
  PickupGame,
  CourtPOI,
  ChatMessage,
  AppNotification,
  AdminAnalytics,
  SportType,
  UserLevel,
  OfflineQueueItem,
  HighlightClip,
  Tournament,
  TournamentTeam,
  TournamentMatch,
  PlayerBoxScore,
  Sponsor,
  AuthProviderSettings
} from './types';
import {
  createFreshAthleteProfile,
  subscribeAthletes,
  subscribePickupGames,
  subscribeMessages,
  subscribeCourts,
  subscribeTournaments,
  subscribeNotifications,
  subscribePresence,
  subscribeAuthProviderSettings,
  saveAuthProviderSettingsToFirestore,
  saveNotificationToFirestore,
  hasPendingFriendRequestInFirestore,
  deleteNotificationFromFirestore,
  clearAllNotificationsFromFirestore,
  saveAthleteToFirestore,
  deleteAthleteFromFirestore,
  savePickupGameToFirestore,
  saveMessageToFirestore,
  updateMessageStatusInFirestore,
  deleteDirectConversationFromFirestore,
  deleteChannelConversationFromFirestore,
  saveCourtToFirestore,
  saveTournamentToFirestore,
  getAthleteProfileFromFirestore,
  seedInitialFirestoreDataIfEmpty,
  wipeEntireFirestoreDatabase,
} from './services/firestoreService';
import { INITIAL_SPONSORS } from './data/mockSponsors';

import { getLevelInfo } from './utils/levelUtils';
import { getGameDateInfo } from './utils/dateUtils';
import { checkAndAwardBadges, fireEliteSeriesConfetti, LegendaryNotificationInfo } from './utils/badgeEvaluator';
import { verifyEmailCodeUtility } from './utils/authUtils';
import confetti from 'canvas-confetti';
import { useSportFilterContext } from './context/SportFilterContext';

import { Navbar } from './components/Navbar';
import { Navigation, NavTab } from './components/Navigation';
import { cleanupExpiredHighlightCache } from './utils/cacheCleanup';
import { clearLegacyVideoCache } from './utils/videoStorage';
import { debugLog, logMount, logLazyLoad, markModuleEval, markComponentInit } from './utils/debugLogger';

const CURRENT_USER = createFreshAthleteProfile({ id: '', name: '', email: '' });
const EMPTY_ADMIN_ANALYTICS = {} as AdminAnalytics;

// Trigger module evaluation timer for performance trace
markModuleEval();

// Simple 'Hello World' isolation component to verify React root & base Tailwind styling (with force bg-red-500 test)
const IsolationHelloWorld = ({ onExit }: { onExit?: () => void }) => {
  useEffect(() => {
    debugLog('ISOLATION_VIEW_MOUNT', 'Isolation "Hello World" component rendered successfully into DOM with Tailwind test');
  }, []);

  return (
    <div className="min-h-screen bg-indigo-950 text-white flex flex-col items-center justify-center p-6 font-sans antialiased">
      <div className="max-w-md w-full bg-indigo-900 border border-lime-400/50 rounded-[2.5rem] p-8 shadow-2xl text-center space-y-6">
        {/* Force-applied bg-red-500 container badge to verify Tailwind CSS class injection into CSSOM */}
        <div className="bg-red-500 text-white font-black text-xs uppercase px-4 py-2 rounded-xl shadow-lg border border-red-300 animate-pulse">
          Tailwind CSS CSSOM Test: bg-red-500 Active
        </div>

        <div className="w-20 h-20 mx-auto rounded-2xl bg-lime-400 text-black flex items-center justify-center font-black text-3xl shadow-xl shadow-lime-400/20">
          🏀
        </div>

        <div className="space-y-2">
          <div className="inline-block px-3 py-1 bg-lime-400/20 border border-lime-400/30 rounded-full text-[10px] font-black italic uppercase text-lime-300">
            Playground League Isolation Test
          </div>
          <h1 className="text-3xl font-black italic uppercase text-lime-400 tracking-tight">
            Hello World!
          </h1>
          <p className="text-xs text-indigo-200/90 leading-relaxed font-medium">
            React Root Initialization & Tailwind CSS Configuration verified operating normally.
          </p>
        </div>
        <div className="bg-indigo-950/80 p-3 rounded-2xl border border-white/10 text-left text-[11px] font-mono text-indigo-300 space-y-1">
          <p className="text-lime-400 font-bold">✓ React Root Rendered</p>
          <p className="text-lime-400 font-bold">✓ Tailwind CSS Injected (bg-red-500)</p>
          <p className="text-lime-400 font-bold">✓ Lifecycle Tracing Active</p>
        </div>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 transition cursor-pointer"
          >
            Launch Full Playground League App
          </button>
        )}
      </div>
    </div>
  );
};

// Direct static component imports to eliminate chunk loading delay during initial mount
import { DashboardView } from './components/DashboardView';
import { StatAnalyticsView } from './components/StatAnalyticsView';
import { PickupGamesView } from './components/PickupGamesView';
import { TournamentsView } from './components/TournamentsView';
import { CourtMapView } from './components/CourtMapView';
import { CourtsTab } from './components/CourtsTab';
import { LeaderboardView } from './components/LeaderboardView';
import { VolleyballLeaderboardView } from './components/VolleyballLeaderboardView';
import { Achievements } from './components/Achievements';
import { TeamChatView } from './components/TeamChatView';
import { ProfileView } from './components/ProfileView';
import { motion, AnimatePresence } from 'motion/react';
import { useSportFilter } from './hooks/useSportFilter';

import { HighlightReelView } from './components/HighlightReelView';
import { SocialFeedView } from './components/SocialFeedView';
import { ScoutsView } from './components/ScoutsView';
import { AdminDashboardView } from './components/AdminDashboardView';
import { PublicAuthView } from './components/PublicAuthView';
import { ResetPasswordView } from './components/ResetPasswordView';
import { EmailVerificationBanner, VerificationGatedModal } from './components/EmailVerificationBanner';
import { WarmupView } from './components/WarmupView';
import { ReferralModal } from './components/ReferralModal';

import { StatLogModal } from './components/StatLogModal';
import { HostGameModal } from './components/HostGameModal';
import { CreateParkModal } from './components/CreateParkModal';
import { NotificationDrawer } from './components/NotificationDrawer';
import { AuthModal } from './components/AuthModal';
import { SignUpModal } from './components/SignUpModal';
import { UploadHighlightModal } from './components/UploadHighlightModal';
import { MiniGamesModal } from './components/MiniGamesModal';
import { ProCheckoutModal } from './components/ProCheckoutModal';
import { StripePaymentStatusTracker } from './components/StripePaymentStatusTracker';
import { LocationOnboardingModal } from './components/LocationOnboardingModal';
import { ConfettiExplosion } from './components/ConfettiExplosion';

import { CheckCircle2, AlertCircle, Wifi, Zap, Crown, Sparkles, X, Trophy } from 'lucide-react';

const ViewLoadingFallback = () => {
  const [progress, setProgress] = useState(15);
  const [statusMessage, setStatusMessage] = useState('Connecting to Court Network...');

  useEffect(() => {
    debugLog('SUSPENSE_FALLBACK_MOUNT', 'ViewLoadingFallback displayed while loading view');

    const timer1 = setTimeout(() => {
      setProgress(55);
      setStatusMessage('Syncing Firestore Database & Athlete Stats...');
    }, 300);

    const timer2 = setTimeout(() => {
      setProgress(90);
      setStatusMessage('Rendering Live Court Roster & Analytics...');
    }, 700);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      debugLog('SUSPENSE_FALLBACK_UNMOUNT', 'ViewLoadingFallback unmounted');
    };
  }, []);

  return (
    <div className="w-full min-h-[420px] flex flex-col items-center justify-center p-8 space-y-6 animate-fadeIn">
      <div className="relative flex items-center justify-center">
        {/* Radar Pulse Outer Rings */}
        <div className="absolute w-28 h-28 rounded-full border border-lime-400/20 animate-ping" />
        <div className="absolute w-20 h-20 rounded-full border-2 border-emerald-400/30 animate-pulse" />

        {/* Center Court Icon */}
        <div className="relative w-16 h-16 rounded-3xl bg-gradient-to-tr from-lime-400 via-emerald-400 to-cyan-400 p-0.5 shadow-xl shadow-lime-400/20">
          <div className="w-full h-full bg-indigo-950 rounded-[22px] flex items-center justify-center">
            <Trophy className="w-8 h-8 text-lime-400 animate-bounce" />
          </div>
        </div>

        <div className="absolute -top-1 -right-1 w-6 h-6 bg-lime-400 rounded-full border-2 border-indigo-950 flex items-center justify-center animate-spin">
          <Zap className="w-3 h-3 text-black fill-black" />
        </div>
      </div>

      <div className="text-center space-y-1 max-w-sm">
        <h3 className="text-sm font-black italic uppercase tracking-wider text-white flex items-center justify-center space-x-1.5">
          <Wifi className="w-4 h-4 text-lime-400 animate-pulse" />
          <span>Court Sync Active</span>
        </h3>
        <p className="text-xs font-semibold text-indigo-200/80">
          {statusMessage}
        </p>
      </div>

      {/* Progress Bar Container */}
      <div className="w-full max-w-xs space-y-2">
        <div className="w-full bg-indigo-950/80 h-2 rounded-full overflow-hidden border border-white/10 p-0.5 shadow-inner">
          <div
            className="bg-gradient-to-r from-lime-400 via-emerald-400 to-cyan-400 h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between items-center text-[10px] font-black italic uppercase text-indigo-300/70 tracking-widest px-1">
          <span>Firestore Sync</span>
          <span className="text-lime-400">{progress}%</span>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  markComponentInit();

  const [isIsolationActive, setIsIsolationActive] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('testIsolation') === 'true' || window.location.hash === '#test-hello-world';
    }
    return false;
  });

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    const isLogged = localStorage.getItem('playground_is_logged_in') === 'true';
    debugLog('STATE_INIT', `isLoggedIn initial state: ${isLogged}`);
    return isLogged;
  });

  const [activeTab, setActiveTab] = useState<NavTab>(() => {
    const isLogged = localStorage.getItem('playground_is_logged_in') === 'true';
    const tab = isLogged ? 'dashboard' : 'auth';
    debugLog('STATE_INIT', `activeTab initial state: ${tab}`);
    return tab;
  });

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('app_dark_mode');
    return saved !== null ? saved === 'true' : true;
  });
  const [activePalette, setActivePalette] = useState<string>(() => {
    return localStorage.getItem('app_theme_palette') || 'midnight_chrome';
  });
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const { activeSportFilter, setActiveSportFilter, isFiltering } = useSportFilterContext();

  useEffect(() => {
    logMount('App', {
      isLoggedIn,
      activeTab,
      isIsolationActive,
      isDarkMode,
      activePalette,
    });
  }, []);

  useEffect(() => {
    // Restore superadmin session if present
    try {
      const savedSession = sessionStorage.getItem('playground_superadmin_session');
      if (savedSession) {
        const parsed = JSON.parse(savedSession);
        if (parsed.authenticated) {
          setIsAdminAuthenticated(true);
        }
      }
    } catch (e) { }

    // Route listener for /superadmin path and /reset-password path
    const handleRouteCheck = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      const search = window.location.search;

      if (path === '/superadmin' || hash === '#superadmin') {
        setActiveTab('superadmin');
      } else if (path === '/reset-password' || search.includes('oobCode') || search.includes('action=resetPassword')) {
        setActiveTab('reset-password');
      }
    };

    handleRouteCheck();
    window.addEventListener('popstate', handleRouteCheck);

    // Routine to clear expired cached highlight data (> 30 days) from localStorage
    const result = cleanupExpiredHighlightCache();
    if (result.clearedCount > 0) {
      console.log(`[Cache Cleanup] Pruned ${result.clearedCount} expired highlight entries (${result.freedBytes} bytes freed).`);
    }
    clearLegacyVideoCache();

    return () => window.removeEventListener('popstate', handleRouteCheck);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activePalette);
    localStorage.setItem('app_theme_palette', activePalette);
  }, [activePalette]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
    localStorage.setItem('app_dark_mode', String(isDarkMode));
  }, [isDarkMode]);

  const getAppBackgroundClass = (palette: string, isDark: boolean) => {
    if (!isDark) {
      return 'bg-slate-100 text-slate-900 transition-colors duration-300 font-sans antialiased selection:bg-lime-400 selection:text-black light-mode';
    }
    switch (palette) {
      case 'neon_court':
        return 'bg-[#030712] text-white transition-colors duration-300 font-sans antialiased selection:bg-cyan-400 selection:text-black dark-mode';
      case 'sunset_horizon':
        return 'bg-[#140b12] text-white transition-colors duration-300 font-sans antialiased selection:bg-amber-400 selection:text-black dark-mode';
      case 'cyber_arena':
        return 'bg-[#041410] text-white transition-colors duration-300 font-sans antialiased selection:bg-emerald-400 selection:text-black dark-mode';
      case 'midnight_chrome':
      default:
        return 'bg-indigo-950 text-white transition-colors duration-300 font-sans antialiased selection:bg-lime-400 selection:text-black dark-mode';
    }
  };

  const [pendingQueue, setPendingQueue] = useState<OfflineQueueItem[]>([]);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [isAuthInitializing, setIsAuthInitializing] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authRetryCount, setAuthRetryCount] = useState<number>(0);
  const [publicAuthMode, setPublicAuthMode] = useState<'register' | 'login'>('register');
  const [isVerificationGatedOpen, setIsVerificationGatedOpen] = useState(false);
  const [gatedFeatureName, setGatedFeatureName] = useState('Competitive Feature');
  const [authProviderSettings, setAuthProviderSettings] = useState<AuthProviderSettings>({
    googleEnabled: false,
    facebookEnabled: false,
    appleEnabled: false,
  });
  const [activeStripeTrackingSessionId, setActiveStripeTrackingSessionId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      try {
        const search = window.location.search;
        if (search.includes('pro_success=true') || search.includes('session_id=')) {
          const params = new URLSearchParams(search);
          const sId = params.get('session_id');
          if (sId) {
            sessionStorage.setItem('pending_stripe_session_id', sId);
            return sId;
          }
        }
        if (window.location.hash) {
          const cleanHash = window.location.hash.replace(/^[#?]+/, '');
          const hashParams = new URLSearchParams(cleanHash);
          const hashSession = hashParams.get('session_id');
          if (hashSession) {
            sessionStorage.setItem('pending_stripe_session_id', hashSession);
            return hashSession;
          }
        }
      } catch (e) {
        console.warn('Initial session resolution notice:', e);
      }
    }
    return null;
  });

  // Main Data States
  const [user, setUser] = useState<AthleteProfile>(() => {
    const isLogged = localStorage.getItem('playground_is_logged_in') === 'true';
    if (isLogged) {
      const saved = localStorage.getItem('playground_current_user');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.id) {
            debugLog('USER_INIT', `Loaded saved user profile for ${parsed.name} (${parsed.id})`);
            return parsed;
          }
        } catch (e) {
          console.error('Failed to parse saved user', e);
        }
      }
      debugLog('USER_INIT', 'isLoggedIn is true but saved profile missing/corrupt; falling back to CURRENT_USER');
      return CURRENT_USER;
    }
    debugLog('USER_INIT', 'App initialized in guest mode (unauthenticated)');
    return CURRENT_USER;
  });

  const userRef = useRef(user);
  const lastSavedUserJsonRef = useRef<string>('');

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (isLoggedIn && user) {
      const userJson = JSON.stringify(user);
      if (lastSavedUserJsonRef.current !== userJson) {
        lastSavedUserJsonRef.current = userJson;
        try {
          localStorage.setItem('playground_current_user', userJson);
        } catch (e) {
          console.error('LocalStorage write error:', e);
        }
        saveAthleteToFirestore(user);
      }
    }
  }, [user, isLoggedIn]);

  // Protective layer: prevent access to Leaderboard, Analytics, and Tournament views if emailVerified is false
  useEffect(() => {
    const protectedTabs: NavTab[] = ['leaderboard', 'volleyball_leaderboard', 'analytics', 'tournaments'];
    if (isLoggedIn && protectedTabs.includes(activeTab) && !user?.emailVerified) {
      let featureName = 'Leaderboards & Global Rankings';
      if (activeTab === 'analytics') featureName = 'Analytics & Advanced Performance Stats';
      if (activeTab === 'tournaments') featureName = 'Tournaments & Playoff Brackets';
      setGatedFeatureName(featureName);
      setIsVerificationGatedOpen(true);
      setActiveTab('dashboard');
    }
  }, [activeTab, isLoggedIn, user?.emailVerified]);

  // Keep unverified accounts on the verification screen until OTP/email verification succeeds.
  useEffect(() => {
    if (!isLoggedIn || !user || user.emailVerified || activeTab === 'admin' || activeTab === 'superadmin' || activeTab === 'reset-password') {
      return;
    }

    if (activeTab !== 'auth') {
      setActiveTab('auth');
    }
    setGatedFeatureName('your Playground League account');
    setIsVerificationGatedOpen(true);
  }, [activeTab, isLoggedIn, user?.emailVerified]);

  // Seed Firestore on startup if empty & subscribe to real-time collections for all users
  useEffect(() => {
    seedInitialFirestoreDataIfEmpty();

    const unsubAthletes = subscribeAthletes((remoteAthletes) => {
      setAthletes(remoteAthletes);
      if (remoteAthletes.length > 0) {
        setUser((currentUser) => {
          if (!currentUser || !currentUser.id) return currentUser;
          const match = remoteAthletes.find(
            (a) => a.id === currentUser.id || (currentUser.email && a.email?.toLowerCase() === currentUser.email.toLowerCase())
          );
          if (match) {
            const wasUnverified = !currentUser.emailVerified && Boolean(match.emailVerified);
            const merged = { ...currentUser, ...match };
            if (wasUnverified) {
              setToastMessage('🎉 Email verified! Competitive leaderboards, tournaments, and pickup hosting are unlocked.');
              triggerConfetti();
            }
            const mergedJson = JSON.stringify(merged);
            if (JSON.stringify(currentUser) === mergedJson) {
              return currentUser;
            }
            lastSavedUserJsonRef.current = mergedJson;
            try {
              localStorage.setItem('playground_current_user', mergedJson);
            } catch (e) { }
            return merged;
          }
          return currentUser;
        });
      }
    });

    const unsubGames = subscribePickupGames((remoteGames) => {
      const nowMs = Date.now();
      const activeOrFutureGames = remoteGames.filter((game) => {
        if ((game as any).status === 'completed' || (game as any).status === 'past') return false;

        const dateInfo = getGameDateInfo(game.dateTime);
        if (dateInfo.isPast) {
          debugLog('GAME_FILTER', `Game ${game.id} excluded: getGameDateInfo isPast is true for ${game.dateTime}`);
          return false;
        }

        if (typeof game.dateTime === 'string') {
          const parsedTime = new Date(game.dateTime).getTime();
          if (!isNaN(parsedTime) && parsedTime < nowMs - 2 * 60 * 60 * 1000) {
            debugLog('GAME_FILTER', `Game ${game.id} excluded: parsedTime (${parsedTime}) < nowMs (${nowMs})`);
            return false;
          }
        }

        return true;
      });
      setPickupGames(activeOrFutureGames);
    });

    const unsubMessages = subscribeMessages((remoteMessages) => {
      setMessages(remoteMessages);
    });

    const unsubCourts = subscribeCourts((remoteCourts) => {
      if (remoteCourts.length > 0) {
        setCourts(remoteCourts);
      }
    });

    const unsubTournaments = subscribeTournaments((remoteTournaments) => {
      setTournaments(remoteTournaments);
    });

    const currentUser = userRef.current;

    const unsubNotifications = subscribeNotifications(
      currentUser?.id || '',
      (remoteNotifications) => {
        const currentUser = userRef.current;
        let clearedLocalSet = new Set<string>();
        try {
          const raw = localStorage.getItem('playground_cleared_notif_ids');
          if (raw) clearedLocalSet = new Set(JSON.parse(raw));
        } catch (e) { }

        let storedUserClearedIds: string[] = [];
        let storedLastClearedTs: number | undefined;
        try {
          const userRaw = localStorage.getItem('playground_current_user');
          if (userRaw) {
            const parsedUser = JSON.parse(userRaw);
            if (Array.isArray(parsedUser.clearedNotificationIds)) storedUserClearedIds = parsedUser.clearedNotificationIds;
            if (parsedUser.lastClearedTimestamp) storedLastClearedTs = parsedUser.lastClearedTimestamp;
          }
        } catch (e) { }

        const userClearedIds = new Set([
          ...(currentUser?.clearedNotificationIds || []).map(String),
          ...storedUserClearedIds.map(String),
        ]);

        const lastClearedTs = currentUser?.lastClearedTimestamp || storedLastClearedTs;
        const lastClearedTime = typeof lastClearedTs === 'number'
          ? lastClearedTs
          : lastClearedTs
            ? new Date(lastClearedTs).getTime()
            : 0;

        const filtered = remoteNotifications.filter((n) => {
          if (n.cleared) {
            debugLog('NOTIF_EXCLUDED', `Notification ${n.id} ("${n.title}") excluded: document flagged cleared=true`);
            return false;
          }
          if (clearedLocalSet.has(n.id)) {
            debugLog('NOTIF_EXCLUDED', `Notification ${n.id} ("${n.title}") excluded: found in clearedLocalSet`);
            return false;
          }
          if (userClearedIds.has(n.id)) {
            debugLog('NOTIF_EXCLUDED', `Notification ${n.id} ("${n.title}") excluded: found in userClearedIds`);
            return false;
          }

          if (lastClearedTime > 0) {
            let notifTime = 0;
            if (n.createdAt) {
              notifTime = typeof n.createdAt === 'number'
                ? n.createdAt
                : new Date(n.createdAt).getTime();
            } else if (n.timestamp) {
              const parsed = new Date(n.timestamp).getTime();
              notifTime = isNaN(parsed) ? 0 : parsed;
            }

            if (notifTime > 0 && notifTime <= lastClearedTime) {
              debugLog('NOTIF_EXCLUDED', `Notification ${n.id} ("${n.title}") excluded: timestamp ${notifTime} <= lastClearedTime ${lastClearedTime}`);
              return false;
            }
          }

          return true;
        });

        debugLog('NOTIF_FILTER_SUMMARY', `Filtered ${remoteNotifications.length} remote notifications -> ${filtered.length} remaining active.`);
        setNotifications(filtered);
      });

    const unsubAuthSettings = subscribeAuthProviderSettings((settings) => {
      setAuthProviderSettings(settings);
    });

    const unsubPresence = subscribePresence((presenceMap) => {
      if (presenceMap && Object.keys(presenceMap).length > 0) {
        setAthletes((prevAthletes) => {
          return prevAthletes.map((ath) => {
            const pres = presenceMap[ath.id];
            if (pres) {
              return {
                ...ath,
                isOnline: pres.isOnline,
                lastActiveTimestamp: pres.lastActive ? (typeof pres.lastActive === 'number' ? pres.lastActive : new Date(pres.lastActive).getTime()) : ath.lastActiveTimestamp,
              };
            }
            return ath;
          });
        });
      }
    });

    return () => {
      unsubAthletes();
      unsubGames();
      unsubMessages();
      unsubCourts();
      unsubTournaments();
      unsubNotifications();
      unsubAuthSettings();
      unsubPresence();
    };
  }, []);

  // Firebase Auth listener with session token verification, silent Firestore re-verification & error boundary fallback
  useEffect(() => {
    setAuthError(null);
    debugLog('AUTH_INIT', `Initializing Firebase Auth state listener (attempt #${authRetryCount + 1})...`);

    // Safety fallback timer so app NEVER hangs on loading screen if network or Auth callback is delayed
    const initSafetyTimer = setTimeout(() => {
      setIsAuthInitializing((prev) => {
        if (prev) {
          debugLog('AUTH_INIT_TIMEOUT', 'Auth initialization safety timeout triggered after 1500ms; unblocking UI.');
          return false;
        }
        return false;
      });
    }, 1500);

    let unsubscribe = () => { };

    try {
      unsubscribe = onAuthStateChanged(
        auth,
        async (firebaseUser) => {
          try {
            debugLog('AUTH_STATE_CHANGE', `Firebase Auth returned state: ${firebaseUser ? `UID=${firebaseUser.uid}, Email=${firebaseUser.email}` : 'null'}`);
            if (firebaseUser) {
              // Non-blocking ID token verification so network hiccups never delay UI startup
              firebaseUser.getIdTokenResult(false).then((idTokenResult) => {
                debugLog('AUTH_TOKEN_VERIFIED', `Valid ID token obtained for UID=${firebaseUser.uid}, expiresAt=${idTokenResult.expirationTime}`);
              }).catch((tokenErr) => {
                console.warn('Firebase ID token verification warning:', tokenErr);
              });

              // Session Refresh: Silently re-verify if Firestore profile matches authenticated UID with 1200ms timeout race
              debugLog('AUTH_PROFILE_FETCH', `Fetching & re-verifying Firestore profile for UID: ${firebaseUser.uid}`);

              const fetchProfilePromise = (async () => {
                let existing = await getAthleteProfileFromFirestore(firebaseUser.uid);
                if (!existing && firebaseUser.email) {
                  existing = await getAthleteProfileFromFirestore(firebaseUser.email);
                }
                return existing;
              })();

              const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 1200));
              let existing = await Promise.race([fetchProfilePromise, timeoutPromise]);

              // Diagnostic check: verify if session_token retrieved from Firestore matches current Firebase UID
              const storedSessionToken = (existing as any)?.session_token || (existing as any)?.sessionToken;
              if (storedSessionToken && storedSessionToken !== firebaseUser.uid) {
                debugLog('AUTH_SESSION_DISCREPANCY', `Security Warning: Mismatch detected between Firestore session_token (${storedSessionToken}) and Firebase UID (${firebaseUser.uid}). Forcing fresh login.`);
                localStorage.removeItem('playground_is_logged_in');
                localStorage.removeItem('playground_current_user');
                setUser(CURRENT_USER);
                setIsLoggedIn(false);
                setPublicAuthMode('login');
                setActiveTab('auth');
                showToast('Session discrepancy detected. Please sign in again for security verification.');
                clearTimeout(initSafetyTimer);
                setIsAuthInitializing(false);
                return;
              }

              const localSavedUserStr = localStorage.getItem('playground_current_user');
              let localSavedUser: AthleteProfile | null = null;
              if (localSavedUserStr) {
                try { localSavedUser = JSON.parse(localSavedUserStr); } catch (e) { }
              }

              if (existing && existing.id) {
                const mergedUser = localSavedUser && localSavedUser.id === existing.id
                  ? { ...existing, ...localSavedUser, id: existing.id || firebaseUser.uid, email: firebaseUser.email || existing.email, emailVerified: firebaseUser.emailVerified || Boolean(localSavedUser.emailVerified || existing.emailVerified), session_token: firebaseUser.uid, isOnline: true }
                  : { ...existing, id: existing.id || firebaseUser.uid, email: firebaseUser.email || existing.email, emailVerified: firebaseUser.emailVerified || Boolean(existing.emailVerified), session_token: firebaseUser.uid, isOnline: true };

                debugLog('AUTH_SUCCESS', `Loaded & re-verified Firestore profile: ${mergedUser.name} (${mergedUser.id})`);
                setUser(mergedUser || CURRENT_USER);
                setIsLoggedIn(true);
                localStorage.setItem('playground_is_logged_in', 'true');
                localStorage.setItem('playground_current_user', JSON.stringify(mergedUser || CURRENT_USER));
                saveAthleteToFirestore(mergedUser || CURRENT_USER).catch(() => { });

                // If user is unverified in Firebase Auth, dispatch verification email immediately
                if (!firebaseUser.emailVerified && firebaseUser.email && !mergedUser.emailVerified) {
                  sendEmailVerification(firebaseUser).catch((sendErr) => {
                    debugLog('AUTH_VERIFY_DISPATCH_NOTICE', `Initial email verification notice: ${sendErr?.message || sendErr}`);
                  });
                }
              } else if (firebaseUser.uid) {
                // Security rule: Only registered users in athletes collection can log in
                // Check if user was registered in this current session
                const isJustRegistered = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('just_registered_user_id') === firebaseUser.uid;

                if (isJustRegistered) {
                  const fresh = localSavedUser && localSavedUser.email === firebaseUser.email
                    ? { ...localSavedUser, id: firebaseUser.uid, emailVerified: firebaseUser.emailVerified || Boolean(localSavedUser.emailVerified), session_token: firebaseUser.uid }
                    : createFreshAthleteProfile({
                      id: firebaseUser.uid,
                      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Athlete',
                      email: firebaseUser.email || `${firebaseUser.uid}@playground.app`,
                      avatar: firebaseUser.photoURL || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300',
                    });
                  fresh.emailVerified = firebaseUser.emailVerified || Boolean(fresh.emailVerified);
                  (fresh as any).session_token = firebaseUser.uid;

                  debugLog('AUTH_FRESH_PROFILE', `Registered profile active for UID: ${fresh.id}`);
                  saveAthleteToFirestore(fresh || CURRENT_USER).catch(() => { });
                  setUser(fresh || CURRENT_USER);
                  setIsLoggedIn(true);
                  localStorage.setItem('playground_is_logged_in', 'true');
                  localStorage.setItem('playground_current_user', JSON.stringify(fresh || CURRENT_USER));
                } else {
                  debugLog('AUTH_UNREGISTERED_DENIED', `Access Denied: UID ${firebaseUser.uid} is not registered in athletes collection.`);
                  signOut(auth).catch(() => { });
                  localStorage.removeItem('playground_is_logged_in');
                  localStorage.removeItem('playground_current_user');
                  setUser(CURRENT_USER);
                  setIsLoggedIn(false);
                  setPublicAuthMode('login');
                  setActiveTab('auth');
                  showToast('Access Denied: This account is not registered in the Playground League athletes collection. Please sign up to create your profile.');
                  clearTimeout(initSafetyTimer);
                  setIsAuthInitializing(false);
                  return;
                }
              } else {
                debugLog('AUTH_FALLBACK_DEFAULT', 'Firebase user lacks UID, setting default CURRENT_USER profile');
                setUser(CURRENT_USER);
                setIsLoggedIn(false);
              }
            } else {
              debugLog('AUTH_UNAUTHENTICATED', 'Firebase user is null. Checking local persistent session validity...');

              // Explicit disconnect handler: set isOnline to false in Firestore when session ends/logs out
              const activeUser = userRef.current;
              if (activeUser && activeUser.id && activeUser.isOnline) {
                saveAthleteToFirestore({
                  ...activeUser,
                  isOnline: false,
                  lastActiveTimestamp: Date.now(),
                }).catch((e) => console.error('Error updating offline status in Firestore on auth state disconnect:', e));
              }

              const isLocalLoggedIn = localStorage.getItem('playground_is_logged_in') === 'true';
              const localSavedUserStr = localStorage.getItem('playground_current_user');
              if (isLocalLoggedIn && localSavedUserStr) {
                try {
                  const localSavedUser = JSON.parse(localSavedUserStr);
                  if (localSavedUser && localSavedUser.id) {
                    debugLog('AUTH_LOCAL_RESTORE', `Restored verified local session for ${localSavedUser.name}`);
                    setUser(localSavedUser);
                    setIsLoggedIn(true);
                  } else {
                    debugLog('AUTH_LOCAL_CORRUPT', 'Local user profile corrupt; setting default CURRENT_USER');
                    setIsLoggedIn(false);
                    setUser(CURRENT_USER);
                  }
                } catch (e) {
                  debugLog('AUTH_PARSE_ERROR', 'Error parsing local saved user JSON; setting default CURRENT_USER');
                  setIsLoggedIn(false);
                  setUser(CURRENT_USER);
                }
              } else {
                debugLog('AUTH_GUEST', 'No active user session. Falling back to default guest profile (CURRENT_USER)');
                setIsLoggedIn(false);
                setUser(CURRENT_USER);
              }
            }
          } catch (err: any) {
            console.error("Auth state resolution error:", err);
            debugLog('AUTH_RESOLUTION_EX', `Exception during auth state resolution: ${err?.message || err}`);
            setAuthError(err?.message || 'Failed to initialize authentication state or connect to database.');
            setUser(CURRENT_USER);
          } finally {
            clearTimeout(initSafetyTimer);
            setIsAuthInitializing(false);
            debugLog('AUTH_INIT_COMPLETE', 'Auth state initialization finished.');
          }
        },
        (error) => {
          console.error("onAuthStateChanged error callback:", error);
          debugLog('AUTH_SUBSCRIPTION_ERROR', `onAuthStateChanged failed: ${error.message}`);
          setAuthError(error?.message || 'Firebase Authentication service lost connection.');
          clearTimeout(initSafetyTimer);
          setIsAuthInitializing(false);
        }
      );
    } catch (outerErr: any) {
      console.error("Failed to establish onAuthStateChanged listener:", outerErr);
      setAuthError(outerErr?.message || 'Failed to establish Firebase Auth listener.');
      clearTimeout(initSafetyTimer);
      setIsAuthInitializing(false);
    }

    return () => {
      clearTimeout(initSafetyTimer);
      unsubscribe();
    };
  }, [authRetryCount]);

  // Session Disconnect Handler: explicitly sets isOnline to false in Firestore on browser unload or close
  useEffect(() => {
    const handleDisconnect = () => {
      const currentUser = userRef.current;
      if (currentUser && currentUser.id) {
        saveAthleteToFirestore({
          ...currentUser,
          isOnline: false,
          lastActiveTimestamp: Date.now(),
        });
      }
    };

    window.addEventListener('beforeunload', handleDisconnect);
    window.addEventListener('pagehide', handleDisconnect);

    return () => {
      window.removeEventListener('beforeunload', handleDisconnect);
      window.removeEventListener('pagehide', handleDisconnect);
    };
  }, []);

  // Automatic Daily Login Streak Evaluator: updates streak on consecutive day logins and applies Streak Freeze if available
  useEffect(() => {
    if (!isLoggedIn || !user || !user.id) return;

    const todayStr = new Date().toISOString().split('T')[0];
    if (user.lastLoginDate === todayStr) return;

    const lastLogin = user.lastLoginDate || user.lastDailyCheckInDate || user.lastStatLoggedDate;
    let newStreak = user.dailyStreak || 1;
    let freezeUsed = false;
    let updatedFreezeCount = user.streakFreezeCount || 0;
    const currentFreezeHistory = user.streakFreezeHistory || [];
    const newFreezeHistory = [...currentFreezeHistory];
    const currentCheckInHistory = user.checkInHistory || [];
    const newCheckInHistory = currentCheckInHistory.includes(todayStr)
      ? currentCheckInHistory
      : [todayStr, ...currentCheckInHistory];

    if (lastLogin) {
      const lastTime = new Date(`${lastLogin.split('T')[0]}T00:00:00Z`).getTime();
      const todayTime = new Date(`${todayStr}T00:00:00Z`).getTime();
      const diffDays = Math.round((todayTime - lastTime) / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        newStreak = (user.dailyStreak || 0) + 1;
        setToastMessage(`🔥 Daily Streak Active! You're on a ${newStreak}-day streak!`);
      } else if (diffDays === 2 && updatedFreezeCount > 0) {
        // Streak Freeze Protection Triggered! Missed 1 day (yesterday)
        const missedDate = new Date(todayTime - 86400000).toISOString().split('T')[0];
        updatedFreezeCount -= 1;
        freezeUsed = true;
        newStreak = Math.max(1, user.dailyStreak || 1);
        if (!newFreezeHistory.includes(missedDate)) {
          newFreezeHistory.unshift(missedDate);
        }
        setToastMessage(`🛡️ Streak Freeze Activated! Your ${newStreak}-day streak was protected from reset!`);
      } else if (diffDays > 1) {
        newStreak = 1;
        setToastMessage(`⚡ Streak Reset: New 1-day streak started. Buy a Streak Freeze to protect future streaks!`);
      }
    } else {
      newStreak = Math.max(1, user.dailyStreak || 1);
    }

    const newLongest = Math.max(user.longestStreak || 0, newStreak);

    handleUpdateProfile({
      dailyStreak: newStreak,
      longestStreak: newLongest,
      streakFreezeCount: updatedFreezeCount,
      lastLoginDate: todayStr,
      checkInHistory: newCheckInHistory,
      streakFreezeHistory: newFreezeHistory,
    });
  }, [isLoggedIn, user?.id]);

  const [athletes, setAthletes] = useState<AthleteProfile[]>([]);
  const [courts, setCourts] = useState<CourtPOI[]>(() => {
    try {
      const savedCustom = localStorage.getItem('playground_custom_courts');
      if (savedCustom) {
        const parsed = JSON.parse(savedCustom);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const existingIds = new Set<string>();
          const customOnly = parsed.filter((c: CourtPOI) => !existingIds.has(c.id));
          return customOnly;
        }
      }
    } catch (e) {
      console.error('Error loading custom courts from local storage', e);
    }
    return [];
  });
  const [pickupGames, setPickupGames] = useState<PickupGame[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>(() => {
    const saved = localStorage.getItem('playground_sponsors');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) { }
    }
    return INITIAL_SPONSORS;
  });

  useEffect(() => {
    try {
      localStorage.setItem('playground_sponsors', JSON.stringify(sponsors));
    } catch (e) { }
  }, [sponsors]);

  const handleAddSponsor = (newSponsor: Sponsor) => {
    setSponsors((prev) => [newSponsor, ...prev]);
  };

  const handleUpdateSponsor = (sponsorId: string, updates: Partial<Sponsor>) => {
    setSponsors((prev) =>
      prev.map((s) => (s.id === sponsorId ? { ...s, ...updates } : s))
    );
  };

  const handleDeleteSponsor = (sponsorId: string) => {
    setSponsors((prev) => prev.filter((s) => s.id !== sponsorId));
  };

  const handleResetSponsors = () => {
    setSponsors(INITIAL_SPONSORS);
  };
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [adminAnalytics] = useState<AdminAnalytics>(EMPTY_ADMIN_ANALYTICS);
  const [directMessageTargetAthlete, setDirectMessageTargetAthlete] = useState<AthleteProfile | null>(null);
  const unreadChatCount = activeTab === 'chat' ? 0 : isLoggedIn && user?.id
    ? messages.filter((message) => {
      const isReceivedDirectMessage = message.chatType === 'direct' &&
        message.senderId !== user.id &&
        message.recipientId === user.id;
      return isReceivedDirectMessage && message.readStatus !== 'read';
    }).length
    : 0;

  // Global Sport Filter Hook: Intercepts and filters data for user's active sport filter
  const {
    filteredAthletes,
    filteredPickupGames,
    filteredTournaments,
  } = useSportFilter({
    userSport: activeSportFilter || user?.primarySport || 'volleyball',
    athletes,
    pickupGames,
    tournaments,
  });

  // Automatically re-sort friendsList array by lastActiveTimestamp every time the chat tab is opened
  useEffect(() => {
    if (activeTab === 'chat' && user && user.friendsList && user.friendsList.length > 0) {
      const rawList = user.friendsList;
      const sorted = [...rawList].sort((a: any, b: any) => {
        const idA = typeof a === 'string' ? a : a?.id;
        const idB = typeof b === 'string' ? b : b?.id;

        const athA = athletes.find((ath) => ath.id === idA);
        const athB = athletes.find((ath) => ath.id === idB);

        const timeA = (athA as any)?.lastActiveTimestamp || (athA?.isOnline ? Date.now() : 0) || (typeof a === 'object' && a?.lastActiveTimestamp ? a.lastActiveTimestamp : 0);
        const timeB = (athB as any)?.lastActiveTimestamp || (athB?.isOnline ? Date.now() : 0) || (typeof b === 'object' && b?.lastActiveTimestamp ? b.lastActiveTimestamp : 0);

        return timeB - timeA;
      });

      const currentIds = rawList.map((f: any) => (typeof f === 'string' ? f : f?.id));
      const sortedIds = sorted.map((f: any) => (typeof f === 'string' ? f : f?.id));

      if (JSON.stringify(currentIds) !== JSON.stringify(sortedIds)) {
        const updatedUser = { ...user, friendsList: sorted };
        setUser(updatedUser);
        localStorage.setItem('playground_current_user', JSON.stringify(updatedUser));
        saveAthleteToFirestore(updatedUser);
      }
    }
  }, [activeTab, athletes]);

  // Modals & Drawers
  const [isStatLogOpen, setIsStatLogOpen] = useState(false);
  const [statLogPrefilledData, setStatLogPrefilledData] = useState<any>(null);

  const handleOpenStatLog = (prefilledData?: any) => {
    setStatLogPrefilledData(prefilledData || null);
    setIsStatLogOpen(true);
  };
  const [isHostGameOpen, setIsHostGameOpen] = useState(false);
  const [isCreateParkOpen, setIsCreateParkOpen] = useState(false);
  const [selectedCourtForHost, setSelectedCourtForHost] = useState<CourtPOI | undefined>(undefined);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isMiniGamesOpen, setIsMiniGamesOpen] = useState(false);
  const [isProCheckoutOpen, setIsProCheckoutOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isUploadHighlightOpen, setIsUploadHighlightOpen] = useState(false);
  const [highlightPresetTitle, setHighlightPresetTitle] = useState<string | undefined>(undefined);
  const [highlightPresetSport, setHighlightPresetSport] = useState<SportType | undefined>(undefined);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [legendaryModal, setLegendaryModal] = useState<LegendaryNotificationInfo | null>(null);

  const triggerConfetti = () => {
    if (user?.enableConfetti === false) return;
    setShowConfetti(true);
    try {
      confetti({
        particleCount: 110,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#a3e635', '#facc15', '#38bdf8', '#c084fc', '#f43f5e'],
      });
    } catch (e) { }
    setTimeout(() => setShowConfetti(false), 3800);
  };

  // One-click URL Email Verification Listener (?action=verify_email&email=... or ?mode=verifyEmail or ?verified=true)
  useEffect(() => {
    try {
      const search = window.location.search;
      if (search.includes('verification_error=')) {
        const params = new URLSearchParams(search);
        const errType = params.get('verification_error');
        setToastMessage(`⚠️ Email verification link expired or invalid (${errType}). Please request a new code.`);
        window.history.replaceState({}, '', window.location.pathname);
      } else if (search.includes('action=verify_email') || search.includes('mode=verifyEmail') || search.includes('verified=true')) {
        const params = new URLSearchParams(search);
        const emailParam = params.get('email');
        const codeParam = params.get('code');

        if (codeParam && emailParam) {
          verifyEmailCodeUtility(emailParam, codeParam).catch(() => { });
        }

        setUser((currentUser) => {
          if (!currentUser) return currentUser;
          const updated: AthleteProfile = { ...currentUser, emailVerified: true };
          if (emailParam && (!currentUser.email || currentUser.email === 'athlete@playgroundleague.pro')) {
            updated.email = emailParam;
          }
          try {
            localStorage.setItem('playground_current_user', JSON.stringify(updated));
          } catch (e) { }
          saveAthleteToFirestore(updated);
          return updated;
        });

        setToastMessage('🎉 Email address confirmed! Full competitive features, leaderboards, and tournament access unlocked.');
        triggerConfetti();

        // Clean query params from URL address bar without reloading
        window.history.replaceState({}, '', window.location.pathname);
      }

      // One-click Stripe Checkout Success Callback Listener (?pro_success=true&session_id=...)
      const searchStr = window.location.search || '';
      const hashStr = window.location.hash || '';
      if (searchStr.includes('pro_success=true') || searchStr.includes('session_id=') || hashStr.includes('pro_success=true') || hashStr.includes('session_id=')) {
        const rawQuery = searchStr ? searchStr : hashStr.replace(/^[#?]+/, '');
        const params = new URLSearchParams(rawQuery);
        const sessionId = params.get('session_id') || `cs_success_${Date.now()}`;
        try {
          sessionStorage.setItem('pending_stripe_session_id', sessionId);
        } catch (e) { }
        setActiveStripeTrackingSessionId(sessionId);
      }
    } catch (e) {
      console.warn('URL query listener notice:', e);
    }
  }, []);

  // Background Listener & Periodic Polling for user.emailVerified status (no full page reload required)
  useEffect(() => {
    if (!isLoggedIn || !user || user.emailVerified) return;

    const checkVerificationStatus = async () => {
      if (!auth.currentUser) return;
      try {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          debugLog('AUTH_POLLING_VERIFIED', `User email verified dynamically for UID: ${auth.currentUser.uid}`);
          setUser((prev) => {
            if (!prev || prev.emailVerified) return prev;
            const updated: AthleteProfile = { ...prev, emailVerified: true };
            try {
              localStorage.setItem('playground_current_user', JSON.stringify(updated));
            } catch (e) { }
            saveAthleteToFirestore(updated).catch(() => { });
            return updated;
          });
          setToastMessage('🎉 Email verified! Competitive rankings, tournaments, and pickup hosting are now unlocked.');
          triggerConfetti();
        }
      } catch (err) {
        // Silent catch during background polling
      }
    };

    // 1. Periodic background polling every 4 seconds
    const intervalId = setInterval(checkVerificationStatus, 4000);

    // 2. Immediate check when window regains focus (e.g. user clicked link in email tab and switched back)
    const onWindowFocus = () => {
      checkVerificationStatus();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVerificationStatus();
      }
    };

    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoggedIn, user?.emailVerified]);

  // Automated Badge Awarding System: Listens for changes in user.stats and career thresholds
  useEffect(() => {
    if (!isLoggedIn || !user || !user.stats) return;

    const { updatedUser, newlyUnlockedBadges, legendaryModalNotification } = checkAndAwardBadges(user);
    if (newlyUnlockedBadges.length > 0) {
      newlyUnlockedBadges.forEach((badge) => {
        if (user.enableConfetti !== false) {
          triggerConfetti();
        }
        const badgeNotif: AppNotification = {
          id: `notif_badge_${Date.now()}_${badge.id}`,
          title: `🏆 BADGE UNLOCKED: ${badge.name}`,
          message: `${badge.description} Earned +${badge.xpReward || 300} XP!`,
          timestamp: 'Just now',
          type: 'badge_unlocked',
          read: false,
        };
        saveNotificationToFirestore(badgeNotif);
        setNotifications((prev) => [badgeNotif, ...prev]);
        if (user.enableBadgeNotifications !== false) {
          setToastMessage(`🏆 NEW BADGE UNLOCKED: ${badge.name}! (+${badge.xpReward || 300} XP) 🎉`);
        }
      });

      if (legendaryModalNotification) {
        if (user.enableBadgeNotifications !== false) {
          setLegendaryModal(legendaryModalNotification);
        }
        if (user.enableConfetti !== false) {
          fireEliteSeriesConfetti();
        }
      }

      setUser(updatedUser);
      setAthletes((prev) => prev.map((a) => (a.id === updatedUser.id ? updatedUser : a)));
      saveAthleteToFirestore(updatedUser);
    }
  }, [user?.stats, user?.winCount, user?.lossCount]);

  const handleMiniGamesEarnXpAndStats = (xpAmount: number, source: string, statsToAdd?: { pts?: number; makes?: number }) => {
    handleEarnXp(xpAmount, source);
    if (statsToAdd && user) {
      const today = new Date().toISOString().split('T')[0];
      const updatedHistory = [...(user.statsHistory || [])];
      updatedHistory.push({
        id: `stat_minigame_${Date.now()}`,
        date: today,
        sport: 'basketball',
        points: statsToAdd.pts || 2,
        rebounds: 0,
        assists: 0,
        steals: 0,
        blocks: 0,
        result: 'W',
      });
      handleUpdateProfile({
        winCount: (user.winCount || 0) + 1,
        statsHistory: updatedHistory,
      });
    }
  };

  const handleOpenUploadHighlightModal = (presetTitle?: string, presetSport?: SportType) => {
    setHighlightPresetTitle(presetTitle);
    setHighlightPresetSport(presetSport);
    setIsUploadHighlightOpen(true);
  };

  const handleEarnXp = (amount: number, source: string) => {
    if (!user) return;
    triggerHaptic('success');
    const currentXp = user.xp || 0;
    const newXp = currentXp + amount;
    const levelInfo = getLevelInfo(newXp);

    if (levelInfo.level > (user.level || 1)) {
      showToast(`🔥 LEVEL UP! Reached Level ${levelInfo.level} (${levelInfo.levelTitle})! 🎉`);
    } else {
      showToast(`Earned +${amount} XP for ${source}! ⭐`);
    }

    const updated: AthleteProfile = {
      ...user,
      xp: newXp,
      level: levelInfo.level,
      xpToNextLevel: levelInfo.xpRequiredForNextLevel,
      levelTitle: levelInfo.levelTitle as UserLevel,
    };

    setUser(updated);
    setAthletes((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    saveAthleteToFirestore(updated);
  };

  // Safe-Store wrapper: forces synchronous local storage persistence prior to Firestore async write
  const safeStoreUserAndFirestore = (updatedUser: AthleteProfile, options: { allowProDowngrade?: boolean } = {}) => {
    if (!updatedUser) return;

    // Keep only hosted video links in persisted athlete metadata.
    const sanitizedUser: AthleteProfile = {
      ...updatedUser,
      highlights: updatedUser.highlights?.map((clip) => {
        if (clip.videoUrl && (clip.videoUrl.startsWith('data:video') || clip.videoUrl.startsWith('blob:'))) {
          return {
            ...clip,
            videoUrl: '',
          };
        }
        return clip;
      }),
    };

    try {
      localStorage.setItem('playground_current_user', JSON.stringify(sanitizedUser));
      localStorage.setItem('playground_is_logged_in', 'true');
    } catch (e) {
      console.error('Safe-Store localStorage write error:', e);
    }
    saveAthleteToFirestore(sanitizedUser, options);
  };

  // Add Highlight Clip handler
  const handleAddHighlight = (newClip: HighlightClip) => {
    const updatedHighlights = [newClip, ...(user?.highlights || [])];
    const updatedUser: AthleteProfile = {
      ...user,
      highlights: updatedHighlights,
      xp: (user?.xp || 0) + 50,
    };
    setUser(updatedUser);

    setAthletes((prev) =>
      prev.map((a) => (a.id === user?.id ? updatedUser : a))
    );

    safeStoreUserAndFirestore(updatedUser);

    showToast(`Highlight "${newClip.title}" published! (+50 XP) 🎬`);
  };

  // Register New User
  const handleRegisterUser = async (newUserProfile: AthleteProfile) => {
    const freshUser: AthleteProfile = {
      ...createFreshAthleteProfile({
        id: newUserProfile.id || `user_public_${Date.now()}`,
        name: newUserProfile.name,
        email: newUserProfile.email,
        handle: newUserProfile.handle,
        avatar: newUserProfile.avatar,
        primarySport: newUserProfile.primarySport,
        role: newUserProfile.role,
        schoolOrLeague: newUserProfile.schoolOrLeague,
        position: newUserProfile.position,
        jerseyNumber: newUserProfile.jerseyNumber,
        bio: newUserProfile.bio,
      }),
      level: 1,
      xp: 0,
      xpToNextLevel: 1000,
      levelTitle: 'Rookie' as UserLevel,
      winCount: 0,
      lossCount: 0,
      winRate: 0,
      pointsPerGame: 0,
      reboundsPerGame: 0,
      assistsPerGame: 0,
      stealsPerGame: 0,
      blocksPerGame: 0,
      registeredMatchesCount: 0,
      statsHistory: [],
      badges: [],
      highlights: [],
      isPro: false,
      subscriptionTier: 'free',
    };

    setUser(freshUser);
    setAthletes((prev) => [freshUser, ...prev.filter((a) => a.id !== freshUser.id)]);
    setIsLoggedIn(true);
    safeStoreUserAndFirestore(freshUser);
    showToast(`Welcome ${freshUser.name}! Your athlete profile is live 🎉`);
    setActiveTab('profile');
  };

  // Login Success Handler (via Social Provider or Email)
  const handleLoginSuccess = async (provider: string, email?: string) => {
    let targetProfile: AthleteProfile | null = null;
    if (email) {
      targetProfile = await getAthleteProfileFromFirestore(email);
      if (!targetProfile) {
        targetProfile = athletes.find((a) => a.email?.toLowerCase() === email.toLowerCase() || a.id === email) || null;
      }
    }

    if (!targetProfile) {
      showToast(`Access Denied: No registered account found for ${email || 'this user'}. Only pre-registered athletes are permitted to log in. Please sign up to create your profile.`);
      signOut(auth).catch(() => { });
      return;
    }

    setUser(targetProfile);
    setAthletes((prev) => [targetProfile!, ...prev.filter((a) => a.id !== targetProfile!.id)]);
    setIsLoggedIn(true);
    safeStoreUserAndFirestore(targetProfile);
    showToast(`Signed in as ${targetProfile.name} via ${provider}! 🎉`);
    setActiveTab('dashboard');
  };

  // Demo Account Switcher
  const handleSelectDemoAccount = async (athleteId: string) => {
    const selected = athletes.find((a) => a.id === athleteId);
    if (selected) {
      setUser(selected);
      setIsLoggedIn(true);
      localStorage.setItem('playground_is_logged_in', 'true');
      localStorage.setItem('playground_current_user', JSON.stringify(selected));
      await saveAthleteToFirestore(selected);
      showToast(`Signed in as ${selected.name}!`);
      setActiveTab('dashboard');
    }
  };

  // Logout Handler with explicit disconnect handling in Firestore
  const handleLogout = async () => {
    if (user && user.id) {
      const offlineUser = {
        ...user,
        isOnline: false,
        lastActiveTimestamp: Date.now(),
      };
      setUser(offlineUser);
      await saveAthleteToFirestore(offlineUser);
    }
    setIsLoggedIn(false);
    setActiveTab('auth');
    localStorage.removeItem('playground_is_logged_in');
    localStorage.removeItem('playground_current_user');
    signOut(auth).catch(() => { });
    showToast('Logged out of athlete account. Redirected to Public Registration & Sign In page.');
  };

  // Admin Authentication Handlers (Username + Password for /superadmin security)
  const handleAdminLogin = (username: string, pass: string): { success: boolean; message?: string } => {
    const cleanUser = username.trim().toLowerCase();
    const cleanPass = pass.trim();

    const validAdmins = [
      { user: 'superadmin', pass: 'Playground#2026!Secured' },
      { user: 'admin.director', pass: 'LeagueMaster2026!' },
      { user: 'admin', pass: 'admin123' },
    ];

    const match = validAdmins.find((a) => a.user === cleanUser && a.pass === cleanPass);
    if (match) {
      setIsAdminAuthenticated(true);
      try {
        sessionStorage.setItem(
          'playground_superadmin_session',
          JSON.stringify({
            authenticated: true,
            adminUser: cleanUser,
            authenticatedAt: new Date().toISOString(),
          })
        );
      } catch (e) { }
      showToast(`Unlocked Superadmin Oversight Portal as @${cleanUser} 🛡️`);
      return { success: true };
    }
    return { success: false, message: 'Invalid Superadmin username or password credentials.' };
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
    try {
      sessionStorage.removeItem('playground_superadmin_session');
    } catch (e) { }
    showToast('Superadmin session securely terminated.');
    if (window.location.pathname === '/superadmin') {
      window.history.pushState(null, '', '/');
    }
    setActiveTab('dashboard');
  };

  // Admin User Management Handlers
  const handleUpdateUserStatus = (userId: string, status: 'active' | 'suspended' | 'banned', reason?: string) => {
    setAthletes((prev) =>
      prev.map((a) => (a.id === userId ? { ...a, accountStatus: status, suspensionReason: reason } : a))
    );
    if (user.id === userId) {
      setUser((prev) => ({ ...prev, accountStatus: status, suspensionReason: reason }));
    }
    showToast(`Updated account status for user to ${status.toUpperCase()}`);
  };

  const handleDeleteUser = async (userId: string) => {
    setAthletes((prev) => prev.filter((a) => a.id !== userId));
    await deleteAthleteFromFirestore(userId);
    if (user.id === userId) {
      setIsLoggedIn(false);
      setActiveTab('auth');
      localStorage.removeItem('playground_is_logged_in');
      localStorage.removeItem('playground_current_user');
      signOut(auth).catch(() => { });
    }
    showToast('User account permanently deleted from platform.');
  };

  const handleResetUserPassword = (userId: string): string => {
    const tempPass = `PLAYGROUND-${Math.floor(1000 + Math.random() * 9000)}-PASS`;
    setAthletes((prev) =>
      prev.map((a) => (a.id === userId ? { ...a, tempPassword: tempPass } : a))
    );
    showToast(`Temporary password issued for user!`);
    return tempPass;
  };

  const handleUpgradeUserPro = (userId: string, isPro: boolean) => {
    setAthletes((prev) =>
      prev.map((a) => {
        if (a.id === userId) {
          const updated: AthleteProfile = {
            ...a,
            isPro,
            subscriptionTier: isPro ? ('pro' as const) : ('free' as const),
            role: isPro ? ('playground_pro' as const) : (a.role === 'playground_pro' ? ('athlete' as const) : a.role),
            isVerifiedPro: isPro ? a.isVerifiedPro : false,
          };
          saveAthleteToFirestore(updated, { allowProDowngrade: !isPro });
          return updated;
        }
        return a;
      })
    );
    if (user.id === userId) {
      const updatedUser: AthleteProfile = {
        ...user,
        isPro,
        subscriptionTier: isPro ? ('pro' as const) : ('free' as const),
        role: isPro ? ('playground_pro' as const) : (user.role === 'playground_pro' ? ('athlete' as const) : user.role),
        isVerifiedPro: isPro ? user.isVerifiedPro : false,
      };
      setUser(updatedUser);
      localStorage.setItem('playground_current_user', JSON.stringify(updatedUser));
      saveAthleteToFirestore(updatedUser, { allowProDowngrade: !isPro });
    }
    const targetAthlete = athletes.find((a) => a.id === userId);
    showToast(`Updated ${targetAthlete?.name || 'user'}'s account to ${isPro ? 'Playground PRO 👑' : 'Standard Free ❌'}`);
  };

  const handleValidateScoutAccess = (userId: string, isScoutValidated: boolean) => {
    setAthletes((prev) =>
      prev.map((a) => {
        if (a.id === userId) {
          const updated = {
            ...a,
            isScoutValidated,
            scoutValidationStatus: isScoutValidated ? ('approved' as const) : ('none' as const),
          };
          saveAthleteToFirestore(updated);
          return updated;
        }
        return a;
      })
    );
    if (user.id === userId) {
      setUser((prev) => {
        const updated = {
          ...prev,
          isScoutValidated,
          scoutValidationStatus: isScoutValidated ? ('approved' as const) : ('none' as const),
        };
        saveAthleteToFirestore(updated);
        return updated;
      });
    }
    showToast(isScoutValidated ? '👁️ Scout Access Granted & Account Validated!' : '🔒 Scout Access Revoked.');
  };

  const handleResetUserStats = (userId: string) => {
    setAthletes((prev) =>
      prev.map((a) => {
        if (a.id === userId) {
          const updated = {
            ...a,
            winCount: 0,
            lossCount: 0,
            winRate: 0,
            pointsPerGame: 0,
            reboundsPerGame: 0,
            assistsPerGame: 0,
            stealsPerGame: 0,
            blocksPerGame: 0,
            xp: 0,
            level: 1,
            xpToNextLevel: 100,
            levelTitle: 'Rookie Prospect' as any,
            badges: [],
            statsHistory: [],
            registeredMatchesCount: 0,
          };
          saveAthleteToFirestore(updated);
          return updated;
        }
        return a;
      })
    );
    if (user.id === userId) {
      setUser((prev) => {
        const updated = {
          ...prev,
          winCount: 0,
          lossCount: 0,
          winRate: 0,
          pointsPerGame: 0,
          reboundsPerGame: 0,
          assistsPerGame: 0,
          stealsPerGame: 0,
          blocksPerGame: 0,
          xp: 0,
          level: 1,
          xpToNextLevel: 100,
          levelTitle: 'Rookie Prospect' as any,
          badges: [],
          statsHistory: [],
          registeredMatchesCount: 0,
        };
        saveAthleteToFirestore(updated);
        return updated;
      });
    }
    showToast(`Reset all match stats & level XP for user!`);
  };

  const handleResetEntireSite = async () => {
    // Wipe Firestore database completely
    await wipeEntireFirestoreDatabase();

    // Clear local storage
    try {
      localStorage.clear();
    } catch (e) {
      console.warn('LocalStorage clear error', e);
    }

    // Sign out Firebase Auth
    signOut(auth).catch(() => { });

    // Reset local state
    setAthletes([]);
    setPickupGames([]);
    setNotifications([]);
    setMessages([]);

    setIsLoggedIn(false);
    setActiveTab('auth');

    showToast('💥 SITE RESET COMPLETE: Firestore database & user profiles wiped for live release.');
  };

  // Tab change handler with auth check & email verification protective gating
  const handleTabChange = (tab: NavTab) => {
    if (tab === 'admin') {
      setActiveTab('admin');
      return;
    }
    if (!isLoggedIn && tab !== 'auth') {
      showToast('🔒 Please sign in or register to access athlete features.');
      setActiveTab('auth');
      return;
    }

    const protectedTabs: NavTab[] = ['leaderboard', 'volleyball_leaderboard', 'analytics', 'tournaments'];
    if (protectedTabs.includes(tab) && isLoggedIn && !user?.emailVerified) {
      let featureName = 'Leaderboards & Global Rankings';
      if (tab === 'analytics') featureName = 'Analytics & Advanced Performance Stats';
      if (tab === 'tournaments') featureName = 'Tournaments & Playoff Brackets';
      setGatedFeatureName(featureName);
      setIsVerificationGatedOpen(true);
      return;
    }

    setActiveTab(tab);
  };

  // Update user profile handler with full persistence using Safe-Store wrapper
  const handleUpdateProfile = (updatedFields: Partial<AthleteProfile>) => {
    const updatedUser: AthleteProfile = {
      ...user,
      ...updatedFields,
    };
    setUser(updatedUser);
    setAthletes((prev) => {
      const exists = prev.some((a) => a.id === updatedUser.id);
      if (exists) {
        return prev.map((a) => (a.id === updatedUser.id ? updatedUser : a));
      }
      return [updatedUser, ...prev];
    });
    const isExplicitProCancellation = updatedFields.isPro === false &&
      updatedFields.isVerifiedPro === false &&
      updatedFields.subscriptionTier === 'free';
    safeStoreUserAndFirestore(updatedUser, { allowProDowngrade: isExplicitProCancellation });
  };

  // Favorite Court Toggle Handler
  const handleToggleFavoriteCourt = (courtId: string) => {
    if (!user) return;
    const currentFavs = user.favoriteCourtIds || [];
    const isFav = currentFavs.includes(courtId);
    const updatedFavs = isFav
      ? currentFavs.filter((id) => id !== courtId)
      : [...currentFavs, courtId];

    const targetCourt = courts.find((c) => c.id === courtId);
    const courtName = targetCourt ? targetCourt.name : 'Court';

    showToast(
      isFav
        ? `Removed "${courtName}" from saved favorites.`
        : `Saved "${courtName}" to your favorite locations! ❤️`
    );

    const updatedUser: AthleteProfile = {
      ...user,
      favoriteCourtIds: updatedFavs,
    };

    setUser(updatedUser);
    setAthletes((athletesList) => athletesList.map((a) => (a.id === updatedUser.id ? updatedUser : a)));
    try {
      localStorage.setItem('playground_current_user', JSON.stringify(updatedUser));
    } catch (e) { }
    saveAthleteToFirestore(updatedUser);
  };

  // Friend Request Handlers
  const handleSendFriendRequest = async (player: { id: string; name: string; avatar?: string; detail?: string }) => {
    if (!user || !user.id) {
      showToast('Please log in to send friend requests! 🔒');
      return;
    }

    if (user.id === player.id) {
      showToast('You cannot send a friend request to yourself! 😄');
      return;
    }

    // 1. Check if already connected as friends
    if (user.friendsList?.includes(player.id)) {
      showToast(`You and ${player.name} are already connected! 🤝`);
      return;
    }

    // 2. State-based check: Check if pending request exists in local state
    const existingLocal = notifications.find(
      (n) =>
        n.type === 'friend_request' &&
        n.status === 'pending' &&
        ((n.senderId === user.id && n.recipientId === player.id) ||
          (n.senderId === player.id && n.recipientId === user.id))
    );
    if (existingLocal) {
      showToast(`Friend request with ${player.name} is already pending! 🤝`);
      return;
    }

    // 3. Firestore check: Verify if pending request exists in Firestore before creating a new one
    try {
      const isPendingInFirestore = await hasPendingFriendRequestInFirestore(user.id, player.id);
      if (isPendingInFirestore) {
        showToast(`A pending friend request with ${player.name} already exists in Firestore! 🤝`);
        return;
      }
    } catch (e) {
      console.warn('Firestore friend request pre-check failed, continuing:', e);
    }

    // 4. Single notification targeted strictly to recipient
    const recipientNotif: AppNotification = {
      id: `notif_fr_${user.id || 'me'}_${player.id}_${Date.now()}`,
      title: '🤝 Friend Request Received!',
      message: `${user?.name || 'An athlete'} sent you a friend request. Accept to connect and view stats!`,
      timestamp: 'Just now',
      createdAt: Date.now(),
      type: 'friend_request',
      read: false,
      recipientId: player.id,
      senderId: user?.id || 'guest',
      senderName: user?.name || 'Athlete',
      senderAvatar: user?.avatar || '',
      status: 'pending',
    };

    saveNotificationToFirestore(recipientNotif);

    showToast(`Friend request sent to ${player.name}! Notification delivered. 🤝`);
  };

  const handleDirectMessageAthlete = (athlete: AthleteProfile) => {
    setDirectMessageTargetAthlete(athlete);
    setActiveTab('chat');
    showToast(`Opened Direct Message with ${athlete.name} 💬`);
  };

  const handleAcceptFriendRequest = (notifId: string, senderName?: string) => {
    let targetSenderId: string | undefined;
    let targetSenderName = senderName;
    let targetSenderAvatar: string | undefined;

    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id === notifId) {
          const updated = { ...n, status: 'accepted' as const, read: true };
          saveNotificationToFirestore(updated);
          targetSenderId = n.senderId;
          targetSenderName = n.senderName || senderName;
          targetSenderAvatar = n.senderAvatar;
          return updated;
        }
        return n;
      })
    );

    let sender = athletes.find(
      (a) =>
        (targetSenderId && a.id === targetSenderId) ||
        (targetSenderName && a.name.toLowerCase() === targetSenderName.toLowerCase())
    );

    if (!sender && targetSenderName) {
      const fallbackId = targetSenderName === 'Jordan Bell' ? 'user_jb' : `user_friend_${Date.now()}`;
      sender = {
        ...createFreshAthleteProfile({
          id: fallbackId,
          name: targetSenderName,
          email: `${targetSenderName.toLowerCase().replace(/[^a-z0-9]/g, '')}@playgroundapp.io`,
          avatar: targetSenderAvatar || 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=400',
          primarySport: 'basketball',
        }),
        lastActiveTimestamp: Date.now(),
        friendsList: [user.id],
      };
      setAthletes((prev) => [sender!, ...prev]);
      saveAthleteToFirestore(sender);
    }

    if (sender) {
      const senderId = sender.id;
      const currentNow = Date.now();

      // Ensure sender and current user have active timestamps
      const updatedSenderWithTime: AthleteProfile = {
        ...sender,
        lastActiveTimestamp: sender.lastActiveTimestamp || currentNow,
        isOnline: true,
      };

      const allAthletesWithSender = [...athletes, updatedSenderWithTime];

      const getActiveTime = (fid: string) => {
        if (fid === user.id) return user.lastActiveTimestamp || currentNow;
        if (fid === senderId) return updatedSenderWithTime.lastActiveTimestamp || currentNow;
        const found = allAthletesWithSender.find((a) => a.id === fid);
        if (!found) return 0;
        return found.lastActiveTimestamp || (found.isOnline ? currentNow : 0);
      };

      setUser((prev) => {
        const currentFriends = (prev.friendsList || []).map((f: any) =>
          typeof f === 'string' ? f : f?.id || String(f)
        );
        const uniqueFriends = Array.from(new Set([...currentFriends, senderId]));

        // Sort friendsList by last active session timestamp (Timestamp check)
        const sortedFriends = [...uniqueFriends].sort((a, b) => getActiveTime(b) - getActiveTime(a));

        const updatedUser = {
          ...prev,
          lastActiveTimestamp: currentNow,
          isOnline: true,
          friendsList: sortedFriends,
        };
        safeStoreUserAndFirestore(updatedUser);
        return updatedUser;
      });

      const senderFriends = (sender.friendsList || []).map((f: any) =>
        typeof f === 'string' ? f : f?.id || String(f)
      );
      const uniqueSenderFriends = Array.from(new Set([...senderFriends, user.id]));
      const sortedSenderFriends = [...uniqueSenderFriends].sort((a, b) => getActiveTime(b) - getActiveTime(a));

      const updatedSender: AthleteProfile = {
        ...updatedSenderWithTime,
        friendsList: sortedSenderFriends,
      };

      setAthletes((prev) => {
        const exists = prev.some((a) => a.id === senderId);
        if (exists) {
          return prev.map((a) => (a.id === senderId ? updatedSender : a));
        } else {
          return [updatedSender, ...prev];
        }
      });

      saveAthleteToFirestore(updatedSender);
    }

    showToast(`Accepted friend request from ${targetSenderName || 'athlete'}! Connected 🎉`);
  };

  const handleDeclineFriendRequest = (notifId: string) => {
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id === notifId) {
          const updated = { ...n, status: 'declined' as const, read: true };
          saveNotificationToFirestore(updated);
          return updated;
        }
        return n;
      })
    );
    showToast('Friend connection request declined.');
  };

  // Add new park handler
  const handleAddCourt = (newCourt: CourtPOI) => {
    const updatedCourts = [newCourt, ...courts];
    setCourts(updatedCourts);

    try {
      const savedCustomStr = localStorage.getItem('playground_custom_courts');
      const customLocal: CourtPOI[] = savedCustomStr ? JSON.parse(savedCustomStr) : [];
      localStorage.setItem('playground_custom_courts', JSON.stringify([newCourt, ...customLocal]));
    } catch (e) {
      console.error('Error updating local custom courts', e);
    }

    saveCourtToFirestore(newCourt);
    const newNotif: AppNotification = {
      id: `notif_park_${Date.now()}`,
      title: '📍 New Park Created',
      message: `"${newCourt.name}" is now live on the map! Pending administrator verification.`,
      timestamp: 'Just now',
      type: 'geofence_alert',
      read: false,
    };
    setNotifications([newNotif, ...notifications]);
    showToast(`Park "${newCourt.name}" added to map (Pending Admin Validation)!`);
  };

  // Validate park handler
  const handleValidateCourt = (courtId: string, action: 'approve' | 'reject') => {
    setCourts((prevCourts) =>
      prevCourts.map((c) => {
        if (c.id === courtId) {
          return {
            ...c,
            status: action === 'approve' ? 'verified' : 'rejected',
          };
        }
        return c;
      })
    );
    if (action === 'approve') {
      showToast('Park officially validated & verified ✓');
    } else {
      showToast('Park submission rejected.');
    }
  };

  // Dark Mode side effect
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Toast feedback helper
  const showToast = (msg: string) => {
    setToastMessage(msg);
    if (msg.toLowerCase().includes('level up')) {
      triggerConfetti();
    }
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Handle Stripe Checkout return URL parameters (e.g., ?session_id=cs_test_...&pro_success=true)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const isProSuccess = urlParams.get('pro_success') === 'true';

    if (sessionId && isProSuccess && user?.id) {
      PaymentProcessor.verifyStripeSession(sessionId, user.id)
        .then((verificationRes) => {
          if (verificationRes.success) {
            PaymentProcessor.updateAthleteProStatus(
              user,
              {
                method: 'stripe',
                receiptId: verificationRes.receiptId,
                amount: 9.99,
                planType: 'monthly_pro',
                userEmail: user.email,
              },
              handleUpdateProfile
            );
            showToast('🏆 Playground PRO Subscription activated via Stripe Checkout!');
            try {
              confetti({
                particleCount: 150,
                spread: 90,
                origin: { y: 0.6 },
                colors: ['#f59e0b', '#a3e635', '#3b82f6', '#ec4899'],
              });
            } catch (err) {
              console.warn('Confetti trigger notice:', err);
            }
          }
        })
        .finally(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    }
  }, [user?.id]);

  // Handle route validation, /register path initialization, referral code detection, and catch-all 404 fallback
  useEffect(() => {
    const pathname = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref') || urlParams.get('referral');
    const isRegisterPath = pathname === '/register' || pathname.startsWith('/register/');

    // Handle referral code storage
    if (refCode) {
      try {
        localStorage.setItem('playground_pending_ref_code', refCode);
      } catch (e) { }
      showToast(`🎉 Referral code "${refCode}" active! Complete registration to claim +100 bonus XP!`);
    }

    // Explicit handling for /register route
    if (isRegisterPath) {
      if (!isLoggedIn) {
        setIsSignUpOpen(true);
      } else {
        showToast(`You are already logged in as ${user?.name || 'Athlete'}. Redirected to Dashboard.`);
        setActiveTab('dashboard');
      }
      try {
        window.history.replaceState({}, document.title, window.location.origin + window.location.hash);
      } catch (e) { }
      return;
    }

    // Catch-all route handler for invalid/unknown paths
    const validPaths = ['/', '', '/superadmin', '/auth', '/login'];
    if (!validPaths.includes(pathname) && !pathname.startsWith('/api/')) {
      showToast(`Path "${pathname}" not found. Redirected to Dashboard.`);
      setActiveTab('dashboard');
      try {
        window.history.replaceState({}, document.title, window.location.origin + window.location.hash);
      } catch (e) { }
    }
  }, [isLoggedIn, user?.name]);

  // Subscribe to Adaptive Background Sync Manager for offline queue & network monitoring
  useEffect(() => {
    const unsubscribe = backgroundSync.subscribe((queueItems, progress) => {
      setPendingQueue(queueItems);
      setIsSyncing(progress.isSyncing);
      setSyncProgress({ current: progress.current, total: progress.total });
    });

    return () => unsubscribe();
  }, []);

  // Helper to add offline queue records and enqueue into backgroundSync
  const handleSimulateOfflineAction = () => {
    const actionTypes: OfflineQueueItem['type'][] = ['LOG_GAME', 'POST_CHAT', 'HOST_GAME', 'UPDATE_PROFILE'];
    const randomType = actionTypes[Math.floor(Math.random() * actionTypes.length)];
    const newItem = backgroundSync.enqueue(randomType, { timestamp: new Date().toISOString(), sport: 'basketball' });
    showToast(`Offline Sync: Enqueued "${randomType}" record (${newItem.id.slice(-6)})`);
  };

  // Handle logging stats & XP progression
  const handleLogStats = (sport: SportType, statsData: any) => {
    const xpEarned = 250;
    const newXp = user.xp + xpEarned;
    let newLevel = user.level;
    let nextXp = user.xpToNextLevel;

    if (newXp >= user.xpToNextLevel) {
      newLevel += 1;
      nextXp += 1000;
      showToast(`🎉 Level Up! You reached Level ${newLevel}!`);
    } else {
      showToast(`+${xpEarned} XP Logged! Keep pushing.`);
    }

    // Update user state
    const updatedStats = { ...user.stats };
    if (sport === 'basketball') {
      const bball = { ...updatedStats.basketball };
      bball.gamesPlayed = (bball.gamesPlayed || 0) + 1;
      bball.pts += statsData.pts || 0;
      bball.ast += statsData.ast || 0;
      bball.reb += statsData.reb || 0;
      bball.stl += statsData.stl || 0;
      bball.blk += statsData.blk || 0;
      bball.threeMade += statsData.threeMade || 0;
      bball.fgMade += Math.round((statsData.pts || 0) / 2);
      bball.fgAttempted += Math.round((statsData.pts || 0) / 2) + 2;
      updatedStats.basketball = bball;
    } else if (sport === 'baseball') {
      const base = { ...updatedStats.baseball };
      base.gamesPlayed = (base.gamesPlayed || 0) + 1;
      base.hits += statsData.hits || 0;
      base.atBats += statsData.atBats || 4;
      base.rbis += statsData.rbis || 0;
      base.homeRuns += statsData.hrs || 0;
      base.battingAvg = Number((base.hits / (base.atBats || 1)).toFixed(3));
      updatedStats.baseball = base;
    } else if (sport === 'softball') {
      const sb = { ...updatedStats.softball };
      sb.gamesPlayed = (sb.gamesPlayed || 0) + 1;
      sb.hits += statsData.hits || 0;
      sb.atBats += statsData.atBats || 4;
      sb.rbis += statsData.rbis || 0;
      sb.stolenBases += statsData.stolenBases || 0;
      sb.battingAvg = Number((sb.hits / (sb.atBats || 1)).toFixed(3));
      updatedStats.softball = sb;
    } else if (sport === 'pickleball') {
      const pb = { ...updatedStats.pickleball };
      pb.gamesPlayed = (pb.gamesPlayed || 0) + 1;
      pb.dinksCount += statsData.dinks || 0;
      pb.acesCount += statsData.aces || 0;
      if (statsData.isWin) pb.matchesWon += 1;
      else pb.matchesLost += 1;
      pb.winRate = Number((pb.matchesWon / (pb.gamesPlayed || 1)).toFixed(3));
      updatedStats.pickleball = pb;
    } else if (sport === 'soccer') {
      const sc = { ...updatedStats.soccer };
      sc.gamesPlayed = (sc.gamesPlayed || 0) + 1;
      sc.goals += statsData.goals || 0;
      sc.assists += statsData.assists || 0;
      if (statsData.isCleanSheet) sc.cleanSheets += 1;
      updatedStats.soccer = sc;
    } else if (sport === 'volleyball') {
      const vb = { ...updatedStats.volleyball };
      vb.gamesPlayed = (vb.gamesPlayed || 0) + 1;
      vb.kills += statsData.kills || 0;
      vb.aces += statsData.aces || 0;
      vb.blocks += statsData.blocks || 0;
      vb.digs += statsData.digs || 0;
      vb.assists = (vb.assists || 0) + (statsData.assists || 0);
      updatedStats.volleyball = vb;
    } else if (sport === 'football') {
      const fb = { ...updatedStats.football };
      fb.gamesPlayed = (fb.gamesPlayed || 0) + 1;
      fb.passingYards += statsData.passingYards || 0;
      fb.rushingYards += statsData.rushingYards || 0;
      fb.touchdowns += statsData.touchdowns || 0;
      updatedStats.football = fb;
    } else if (sport === 'tennis') {
      const tn = { ...updatedStats.tennis };
      tn.gamesPlayed = (tn.gamesPlayed || 0) + 1;
      tn.aces += statsData.aces || 0;
      tn.breakPointsWon += statsData.breakPointsWon || 0;
      updatedStats.tennis = tn;
    }

    // Daily Streak Logic
    const todayStr = new Date().toISOString().split('T')[0];
    let newStreak = user.dailyStreak || 1;

    if (user.lastStatLoggedDate && user.lastStatLoggedDate !== todayStr) {
      const lastTime = new Date(`${user.lastStatLoggedDate.split('T')[0]}T00:00:00Z`).getTime();
      const todayTime = new Date(`${todayStr}T00:00:00Z`).getTime();
      const diffDays = Math.round((todayTime - lastTime) / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        newStreak += 1;
      } else if (diffDays > 1) {
        newStreak = 1;
      }
    }

    // Construct sport-specific history item without cross-sport metric pollution
    const rawGeo = statsData.geolocation || {
      latitude: 34.0522,
      longitude: -118.2437,
      city: user?.registeredCity || 'New York',
      state: user?.registeredState || 'NY',
      locationName: `${user?.registeredCity || 'Local'} Arena Court #1`,
      verifiedAt: new Date().toISOString(),
    };

    const baseHistoryItem = {
      id: `match_${Date.now()}`,
      date: todayStr,
      sport,
      opponent: 'Playground Match',
      isWin: !!statsData.isWin,
      result: statsData.isWin ? 'W' : 'L',
      verified: true,
      latitude: rawGeo.latitude,
      longitude: rawGeo.longitude,
      city: rawGeo.city,
      state: rawGeo.state,
      locationName: rawGeo.locationName,
      geolocation: {
        ...rawGeo,
        verified: true,
        verifiedAt: rawGeo.verifiedAt || new Date().toISOString(),
      },
    };

    let sportSpecificFields: any = {};
    if (sport === 'volleyball') {
      sportSpecificFields = {
        kills: statsData.kills || 0,
        aces: statsData.aces || 0,
        blocks: statsData.blocks || 0,
        digs: statsData.digs || 0,
        assists: statsData.assists || 0,
      };
    } else if (sport === 'basketball') {
      sportSpecificFields = {
        pts: statsData.pts || 0,
        ast: statsData.ast || 0,
        reb: statsData.reb || 0,
        stl: statsData.stl || 0,
        blk: statsData.blk || 0,
        threeMade: statsData.threeMade || 0,
      };
    } else if (sport === 'soccer') {
      sportSpecificFields = {
        goals: statsData.goals || 0,
        assists: statsData.assists || 0,
      };
    } else if (sport === 'baseball' || sport === 'softball') {
      sportSpecificFields = {
        hits: statsData.hits || 0,
        atBats: statsData.atBats || 4,
        rbis: statsData.rbis || 0,
        stolenBases: statsData.stolenBases || 0,
      };
    } else if (sport === 'pickleball') {
      sportSpecificFields = {
        dinks: statsData.dinks || 0,
        aces: statsData.aces || 0,
      };
    } else if (sport === 'football') {
      sportSpecificFields = {
        passingYards: statsData.passingYards || 0,
        touchdowns: statsData.touchdowns || 0,
      };
    } else if (sport === 'tennis') {
      sportSpecificFields = {
        aces: statsData.aces || 0,
        breakPointsWon: statsData.breakPointsWon || 0,
      };
    }

    const newHistoryItem = {
      ...baseHistoryItem,
      ...sportSpecificFields,
    };

    const updatedUser: AthleteProfile = {
      ...user,
      xp: newXp,
      level: newLevel,
      xpToNextLevel: nextXp,
      winCount: statsData.isWin ? user.winCount + 1 : user.winCount,
      lossCount: !statsData.isWin ? user.lossCount + 1 : user.lossCount,
      stats: updatedStats,
      statsHistory: [newHistoryItem, ...(user.statsHistory || [])],
      dailyStreak: newStreak,
      lastStatLoggedDate: todayStr,
    };

    setUser(updatedUser);
    setAthletes(athletes.map((a) => (a.id === updatedUser.id ? updatedUser : a)));

    // Track offline item if offline
    if (!isOnline) {
      setPendingQueue([
        ...pendingQueue,
        {
          id: `sync_${Date.now()}`,
          type: 'LOG_GAME',
          payload: statsData,
          createdAt: new Date().toISOString(),
          status: 'pending',
        },
      ]);
    }
  };

  // Handle hosting new game
  const handleHostGame = (gameData: Partial<PickupGame>) => {
    const emailsCount = gameData.invitedEmails?.length || 0;
    const phonesCount = gameData.invitedPhones?.length || 0;

    const newGame: PickupGame = {
      id: `game_${Date.now()}`,
      title: gameData.title || 'Playground Match',
      sport: gameData.sport || 'basketball',
      courtId: gameData.courtId || courts[0].id,
      courtName: gameData.courtName || courts[0].name,
      address: gameData.address || courts[0].address,
      dateTime: gameData.dateTime || 'Today • 6:00 PM',
      hostName: user?.name || 'Host',
      hostAvatar: user?.avatar || '',
      hostId: user?.id || 'guest',
      level: gameData.level || 'Competitive',
      currentPlayers: 1,
      maxPlayers: gameData.maxPlayers || 10,
      description: gameData.description || 'Pickup run hosted by player.',
      isHostedByMe: true,
      invitedEmails: gameData.invitedEmails || [],
      invitedPhones: gameData.invitedPhones || [],
      inviteCode: gameData.inviteCode,
      inviteUrl: gameData.inviteUrl,
      playersList: [{ id: user?.id || 'guest', name: user?.name || 'Player', avatar: user?.avatar || '', rsvp: 'going' }],
    };

    setPickupGames([newGame, ...pickupGames]);
    savePickupGameToFirestore(newGame);

    let inviteMsg = '';
    if (emailsCount > 0 || phonesCount > 0) {
      inviteMsg = ` Sent ${emailsCount} email & ${phonesCount} SMS invites with direct sign-up links! 📧📱`;
    } else {
      inviteMsg = ' Local players notified.';
    }
    showToast(`🔥 Game Hosted!${inviteMsg}`);

    // Add alert notification
    const newNotif: AppNotification = {
      id: `notif_${Date.now()}`,
      title: '🏀 Hosted Game & Invites Live!',
      message: `"${newGame.title}" posted at ${newGame.courtName}.${emailsCount > 0 || phonesCount > 0
          ? ` Dispatched invite messages to ${emailsCount} emails & ${phonesCount} mobile numbers.`
          : ' Nearby players alerted.'
        }`,
      timestamp: 'Just now',
      type: 'game_invite',
      read: false,
    };
    setNotifications([newNotif, ...notifications]);
  };

  // Join Game RSVP
  const handleJoinGame = (gameId: string) => {
    setPickupGames(
      pickupGames.map((game) => {
        if (game.id === gameId) {
          const alreadyJoined = game.playersList.some((p) => p.id === user.id);
          if (alreadyJoined) {
            showToast('You are already RSVP\'d for this game.');
            return game;
          }

          showToast(`RSVP Confirmed for ${game.title}! ⏰ 30-min reminder set!`);

          // Schedule / Dispatch 30-minute pre-game reminder notification alert
          const reminderNotif: AppNotification = {
            id: `notif_reminder_${game.id}_${Date.now()}`,
            title: `⏰ 30m Pre-Game Alert: "${game.title}"`,
            message: `Reminder alert: Your RSVP'd pickup game at ${game.courtName || 'the court'} starts in 30 minutes (${game.dateTime}). Gear up & head to the court!`,
            timestamp: '30m prior to start',
            type: 'game_reminder',
            read: false,
          };

          setNotifications((prevNotifs) => {
            const updated = [reminderNotif, ...prevNotifs];
            saveNotificationToFirestore(reminderNotif);
            return updated;
          });

          const updatedGame = {
            ...game,
            currentPlayers: game.currentPlayers + 1,
            playersList: [
              ...game.playersList,
              { id: user?.id || 'guest', name: user?.name || 'Player', avatar: user?.avatar || '', rsvp: 'going' as const },
            ],
          };
          savePickupGameToFirestore(updatedGame);
          return updatedGame;
        }
        return game;
      })
    );
  };

  // Tournament Handlers
  const handleCreateTournament = (newTourney: Tournament) => {
    setTournaments([newTourney, ...tournaments]);
    saveTournamentToFirestore(newTourney);
    handleEarnXp(150, 'Organizing Court Tournament');
    showToast(`🏆 Tournament "${newTourney.title}" created! (+150 XP)`);
  };

  const handleRegisterTeam = (tournamentId: string, team: TournamentTeam) => {
    setTournaments(
      tournaments.map((t) => {
        if (t.id === tournamentId) {
          const updatedTeams = [...t.registeredTeams, team];
          return {
            ...t,
            registeredTeams: updatedTeams,
          };
        }
        return t;
      })
    );
    handleEarnXp(75, 'Registering Squad in Tournament');
    showToast(`Registered squad "${team.name}"! (+75 XP) 🏆`);
  };

  const handleUpdateMatchScore = (
    tournamentId: string,
    matchId: string,
    score1: number,
    score2: number,
    winnerId?: string,
    boxScores?: PlayerBoxScore[]
  ) => {
    let targetTournament: Tournament | undefined;
    let targetMatch: TournamentMatch | undefined;

    setTournaments(
      tournaments.map((t) => {
        if (t.id === tournamentId) {
          targetTournament = t;
          const updatedMatches = t.matches.map((m) => {
            if (m.id === matchId) {
              targetMatch = m;
              return {
                ...m,
                score1,
                score2,
                winnerId,
                status: 'completed' as const,
                ...(boxScores && boxScores.length > 0 ? { boxScores } : {}),
              };
            }
            return m;
          });
          const updated = { ...t, matches: updatedMatches };
          saveTournamentToFirestore(updated);
          return updated;
        }
        return t;
      })
    );

    // Automatically send an app notification to all team captains registered in the tournament
    if (targetTournament) {
      const tourney = targetTournament as Tournament;
      const t1Name = targetMatch?.team1?.name || 'Team 1';
      const t2Name = targetMatch?.team2?.name || 'Team 2';
      const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const newNotifications: AppNotification[] = [];

      if (tourney.registeredTeams && tourney.registeredTeams.length > 0) {
        tourney.registeredTeams.forEach((team) => {
          const notifId = `notif_match_score_${tournamentId}_${matchId}_${team.id}_${Date.now()}_${Math.random().toString(36).substring(2, 5)}`;
          const notif: AppNotification = {
            id: notifId,
            title: `🏆 Tournament Score Posted: ${tourney.title}`,
            message: `Match #${targetMatch?.matchNumber || ''} result: ${t1Name} (${score1}) vs ${t2Name} (${score2}). Notified Team Captain ${team.captainName} (${team.name}).`,
            timestamp: timeStr,
            type: 'tournament_update',
            read: false,
            senderName: 'Tournament Admin',
            actionUrl: '#tournaments',
          };
          saveNotificationToFirestore(notif);
          newNotifications.push(notif);
        });
      } else {
        const notifId = `notif_match_score_${tournamentId}_${matchId}_${Date.now()}`;
        const notif: AppNotification = {
          id: notifId,
          title: `🏆 Tournament Score Posted: ${tourney.title}`,
          message: `Match #${targetMatch?.matchNumber || ''} result: ${t1Name} (${score1}) vs ${t2Name} (${score2}).`,
          timestamp: timeStr,
          type: 'tournament_update',
          read: false,
          senderName: 'Tournament Admin',
          actionUrl: '#tournaments',
        };
        saveNotificationToFirestore(notif);
        newNotifications.push(notif);
      }

      setNotifications((prev) => [...newNotifications, ...prev]);
      const captainsCount = tourney.registeredTeams?.length || 0;
      showToast(`Match score updated! Notification sent to ${captainsCount > 0 ? `${captainsCount} team captains` : 'participants'}. ⚡`);
    } else {
      showToast('Match score & player box scores recorded! ⚡');
    }
  };

  const handleUpdateTournamentPrizePool = (tournamentId: string, prizePool: string) => {
    setTournaments(
      tournaments.map((t) => {
        if (t.id === tournamentId) {
          const updated = { ...t, prizePool };
          saveTournamentToFirestore(updated);
          return updated;
        }
        return t;
      })
    );
    showToast(`🏆 Prize Pool updated to "${prizePool}"!`);
  };

  // Real-time Chat Handler
  const handleSendMessage = (
    text: string,
    type: 'text' | 'callout' | 'location' | 'game_result' = 'text',
    locationName?: string,
    imageUrl?: string,
    extra?: Partial<ChatMessage>
  ) => {
    const newMsg: ChatMessage = {
      id: extra?.id || `m_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      senderId: user?.id || 'guest',
      senderName: user?.name || 'Athlete',
      senderAvatar: user?.avatar || '',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sport: user?.primarySport || activeSportFilter || 'basketball',
      type: (extra?.type || type) as any,
      locationName: extra?.locationName || locationName,
      imageUrl: extra?.imageUrl || imageUrl,
      chatType: extra?.chatType || 'channel',
      recipientId: extra?.recipientId,
      groupParticipants: extra?.groupParticipants,
      linkPreview: extra?.linkPreview,
      readStatus: extra?.readStatus || 'sent',
      messageStatus: extra?.messageStatus || 'sent',
      createdAt: extra?.createdAt || Date.now(),
      ...extra,
    };

    setMessages((prev) => {
      const filtered = prev.filter((m) => m.id !== newMsg.id);
      return [...filtered, newMsg];
    });
    saveMessageToFirestore(newMsg);
  };

  const handleMessagesRead = (messageIds: string[]) => {
    if (messageIds.length === 0) return;
    const readIds = new Set(messageIds);
    setMessages((prev) => prev.map((message) =>
      readIds.has(message.id)
        ? { ...message, readStatus: 'read', messageStatus: 'read' }
        : message
    ));
    messageIds.forEach((messageId) => updateMessageStatusInFirestore(messageId, 'read'));
  };

  // Delete Conversation Handler
  const handleDeleteConversation = async (targetId: string, category: 'direct' | 'channel' | 'group') => {
    const currentUserId = user?.id || 'current_user';
    setMessages((prev) =>
      prev.filter((m) => {
        if (category === 'direct') {
          const isDirectToTarget = (m.senderId === currentUserId && m.recipientId === targetId);
          const isDirectFromTarget = (m.senderId === targetId && (m.recipientId === currentUserId || !m.recipientId));
          const isLegacyDirect = m.chatType === 'direct' && (
            (m.senderId === currentUserId && m.recipientId === targetId) ||
            (m.senderId === targetId && m.recipientId === currentUserId) ||
            (m.senderId === targetId && !m.recipientId)
          );
          return !(isDirectToTarget || isDirectFromTarget || isLegacyDirect);
        } else {
          const isMatch = m.gameId === targetId || (targetId.startsWith('channel_') && (!m.chatType || m.chatType === 'channel' || m.chatType === 'group'));
          return !isMatch;
        }
      })
    );

    if (category === 'direct') {
      await deleteDirectConversationFromFirestore(currentUserId, targetId);
    } else {
      await deleteChannelConversationFromFirestore(targetId);
    }
  };

  // Geo-Fence Alert Simulation
  const handleTriggerGeofenceAlert = (court: CourtPOI) => {
    const alertNotif: AppNotification = {
      id: `geo_${Date.now()}`,
      title: '📍 Proximity Radar Alert',
      message: `You are within ${court.geofenceRadiusMeters}m of ${court.name}! ${court.activePlayersNow} active athletes on court right now.`,
      timestamp: 'Just now',
      type: 'geofence_alert',
      read: false,
    };
    setNotifications([alertNotif, ...notifications]);
    showToast(`Radar Alert: ${court.name} is 200m away!`);
  };

  // Share Social Links
  const handleShare = (title: string) => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({
        title: `Playground League - ${title}`,
        text: `Check out ${title}'s official leaderboard rank & stats card on Playground League!`,
        url: window.location.href,
      })
        .then(() => {
          showToast(`🏆 Shared ${title}'s rank card successfully! ✓ (+15 XP)`);
          handleEarnXp(15, 'Shared Athlete Rank Card');
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            if (navigator.clipboard) {
              navigator.clipboard.writeText(window.location.href);
            }
            showToast(`📋 Copied share link for ${title}'s rank card! ✓`);
          }
        });
    } else {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(window.location.href);
      }
      showToast(`📋 Copied social share link for ${title}! ✓`);
    }
  };

  if (isIsolationActive) {
    debugLog('ISOLATION_MODE', 'Rendering Isolation "Hello World" component');
    return <IsolationHelloWorld onExit={() => setIsIsolationActive(false)} />;
  }

  // Auth Listener Error Fallback Boundary Screen
  if (authError) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center p-6 text-center ${getAppBackgroundClass(activePalette, isDarkMode)}`}>
        <div className="bg-indigo-900/90 backdrop-blur-xl p-8 rounded-[2.5rem] border border-rose-500/40 shadow-2xl max-w-md w-full space-y-6 text-white relative animate-fadeIn">
          <div className="w-16 h-16 rounded-3xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center mx-auto text-rose-400">
            <AlertCircle className="w-8 h-8 animate-bounce" />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-black italic uppercase text-white tracking-tight">
              Authentication Initialization Failed
            </h2>
            <p className="text-xs text-rose-200/90 font-semibold leading-relaxed">
              {authError}
            </p>
          </div>

          <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 text-left text-xs space-y-2 text-indigo-200/80">
            <p className="font-bold text-white uppercase text-[10px] tracking-wider">Troubleshooting Steps:</p>
            <ul className="list-disc list-inside space-y-1 text-[11px] text-indigo-200/90">
              <li>Verify your active network / internet connection.</li>
              <li>Re-trigger Firebase authentication sync using the button below.</li>
              <li>You can also continue in Guest Mode while connection restores.</li>
            </ul>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setAuthError(null);
                setIsAuthInitializing(true);
                setAuthRetryCount((prev) => prev + 1);
              }}
              className="w-full py-3.5 px-6 bg-gradient-to-r from-lime-400 via-emerald-400 to-cyan-400 text-black font-black italic uppercase text-xs rounded-2xl shadow-lg shadow-lime-400/20 hover:scale-[1.02] transition flex items-center justify-center space-x-2"
            >
              <Zap className="w-4 h-4 fill-black" />
              <span>Retry Authentication Sync</span>
            </button>

            <button
              onClick={() => {
                setAuthError(null);
                setIsAuthInitializing(false);
                setIsLoggedIn(false);
                setUser(CURRENT_USER);
              }}
              className="w-full py-2.5 px-4 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 text-xs font-bold rounded-xl transition"
            >
              Continue in Guest Mode
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Clean, branded loading screen while Auth resolves profile from Firebase/localStorage
  if (isAuthInitializing) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center p-6 text-center ${getAppBackgroundClass(activePalette, isDarkMode)}`}>
        <div className="relative flex flex-col items-center space-y-6 max-w-sm w-full">
          {/* Animated Logo / Icon */}
          <div className="relative">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-lime-400 via-emerald-400 to-cyan-400 p-0.5 animate-pulse shadow-2xl shadow-lime-400/20">
              <div className="w-full h-full bg-indigo-950 rounded-[22px] flex items-center justify-center">
                <Trophy className="w-10 h-10 text-lime-400 animate-bounce" />
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-lime-400 rounded-full border-2 border-indigo-950 flex items-center justify-center animate-spin">
              <Zap className="w-3 h-3 text-black fill-black" />
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-black italic tracking-tight text-white uppercase">
              PLAYGROUND LEAGUE PRO
            </h2>
            <p className="text-xs font-bold text-indigo-200/70">
              Synchronizing athlete profile & court networks...
            </p>
          </div>

          {/* Loading Indicator */}
          <div className="w-full bg-indigo-900/60 h-1.5 rounded-full overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-lime-400 via-emerald-400 to-cyan-400 h-full w-2/3 animate-pulse rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  const safeUser = user || CURRENT_USER;


  return (
    <div className={`min-h-screen ${getAppBackgroundClass(activePalette, isDarkMode)}`}>
      {/* Navbar Header */}
      <Navbar
        user={safeUser}
        isLoggedIn={isLoggedIn}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        isOnline={isOnline}
        setIsOnline={setIsOnline}
        pendingSyncCount={pendingQueue.length}
        notifications={notifications}
        onOpenNotifications={() => {
          if (!isLoggedIn) {
            showToast('🔒 Sign in to view notifications');
            setPublicAuthMode('login');
            setActiveTab('auth');
            return;
          }
          setIsNotificationsOpen(true);
        }}
        onOpenAuth={() => {
          setPublicAuthMode('login');
          setActiveTab('auth');
          setIsAuthOpen(true);
        }}
        onOpenSignUp={() => {
          setPublicAuthMode('register');
          setActiveTab('auth');
          setIsSignUpOpen(true);
        }}
        onOpenProfile={() => {
          if (!isLoggedIn) {
            showToast('🔒 Sign in to view athlete profile');
            setPublicAuthMode('login');
            setActiveTab('auth');
            return;
          }
          setActiveTab('profile');
        }}
        onOpenReferralModal={() => setIsReferralModalOpen(true)}
        onNavigateAuth={() => {
          setPublicAuthMode('login');
          setActiveTab('auth');
        }}
        onLogout={handleLogout}
        activeSportFilter={activeSportFilter}
        isFiltering={isFiltering}
        onSportFilterChange={(sport) => {
          setActiveSportFilter(sport);
          showToast(`⚡ Swapped primary sport filter to ${sport.toUpperCase()}! Feeds & leaderboards updated.`);
        }}
      />

      {/* Global Email Verification Alert Banner */}
      {isLoggedIn && (
        <EmailVerificationBanner
          userEmail={safeUser.email}
          isVerified={safeUser.emailVerified}
          onVerifiedSimulated={() => {
            handleUpdateProfile({ emailVerified: true });
            setIsVerificationGatedOpen(false);
            setActiveTab('dashboard');
            showToast('Email address verified! Full competitive access unlocked. 🎉');
          }}
        />
      )}

      {/* Main Container Layout */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row">
        {/* Navigation Sidebar */}
        <Navigation
          user={safeUser}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          isLoggedIn={isLoggedIn}
          onOpenStatLog={() => {
            if (!isLoggedIn) {
              showToast('🔒 Sign in or register to log game stats');
              setActiveTab('auth');
              return;
            }
            setIsStatLogOpen(true);
          }}
          onOpenHostGame={() => {
            if (!isLoggedIn) {
              showToast('🔒 Sign in or register to host pickup games');
              setActiveTab('auth');
              return;
            }
            setSelectedCourtForHost(undefined);
            setIsHostGameOpen(true);
          }}
          onOpenReferralModal={() => setIsReferralModalOpen(true)}
          unreadChatCount={unreadChatCount}
          pendingQueue={pendingQueue}
          isSyncing={isSyncing}
          syncProgress={syncProgress}
          isOnline={isOnline}
          onSimulateOfflineAction={handleSimulateOfflineAction}
        />

        {/* Dynamic View Area */}
        <main className="flex-1 p-3 sm:p-6 lg:p-8 min-w-0 pb-24 lg:pb-8">
          {/* Toast Notification Banner */}
          {toastMessage && (
            <div className="fixed bottom-20 lg:bottom-8 right-6 z-50 bg-slate-900 text-white font-bold text-xs px-4 py-3 rounded-2xl shadow-2xl border border-orange-500/50 flex items-center space-x-2 animate-bounce">
              <Zap className="w-4 h-4 text-orange-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          <React.Suspense fallback={<ViewLoadingFallback />}>
            {activeTab === 'admin' || activeTab === 'superadmin' ? (
              <AdminDashboardView
                analytics={adminAnalytics}
                courts={courts}
                athletes={athletes}
                currentUser={safeUser}
                sponsors={sponsors}
                authProviderSettings={authProviderSettings}
                onUpdateAuthProviderSettings={(settings) => {
                  setAuthProviderSettings(settings);
                  saveAuthProviderSettingsToFirestore(settings);
                  showToast('Auth Provider settings updated & saved to Firestore! 🔐');
                }}
                onAddSponsor={handleAddSponsor}
                onUpdateSponsor={handleUpdateSponsor}
                onDeleteSponsor={handleDeleteSponsor}
                onResetSponsors={handleResetSponsors}
                pendingQueue={pendingQueue}
                onValidateCourt={handleValidateCourt}
                onUpdateUserStatus={handleUpdateUserStatus}
                onDeleteUser={handleDeleteUser}
                onResetUserPassword={handleResetUserPassword}
                onUpgradeUserPro={handleUpgradeUserPro}
                onValidateScoutAccess={handleValidateScoutAccess}
                onResetUserStats={handleResetUserStats}
                onResetEntireSite={handleResetEntireSite}
                isAdminAuthenticated={isAdminAuthenticated}
                onAdminLogin={handleAdminLogin}
                onAdminLogout={handleAdminLogout}
              />
            ) : activeTab === 'reset-password' ? (
              <ResetPasswordView
                onBackToApp={() => setActiveTab(isLoggedIn ? 'dashboard' : 'auth')}
              />
            ) : !isLoggedIn || activeTab === 'auth' ? (
              <PublicAuthView
                user={safeUser}
                isAuthInitializing={isAuthInitializing}
                initialAuthMode={publicAuthMode}
                authProviderSettings={authProviderSettings}
                onAuthModeChange={setPublicAuthMode}
                onRegisterUser={handleRegisterUser}
                onSelectDemoAccount={handleSelectDemoAccount}
                onLoginSuccess={handleLoginSuccess}
              />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  className="w-full"
                >
                  {activeTab === 'dashboard' && (
                    <DashboardView
                      user={safeUser}
                      pickupGames={filteredPickupGames}
                      tournaments={filteredTournaments}
                      courts={courts}
                      favoriteCourtIds={safeUser.favoriteCourtIds || []}
                      selectedSport={activeSportFilter}
                      pendingQueue={pendingQueue}
                      onToggleFavoriteCourt={handleToggleFavoriteCourt}
                      onOpenStatLog={handleOpenStatLog}
                      onOpenHostGame={() => setIsHostGameOpen(true)}
                      onOpenMiniGames={() => setIsMiniGamesOpen(true)}
                      onJoinGame={handleJoinGame}
                      onNavigateToTab={handleTabChange}
                      onClaimGoalReward={(goalId) => showToast('Goal claimed! +150 XP added.')}
                      onEarnXp={handleEarnXp}
                      onUpdateProfile={(updated) => {
                        handleUpdateProfile(updated);
                        showToast(updated.isPro ? 'Welcome to Playground PRO Tier! 👑 (+250 XP)' : 'Switched to Free Tier.');
                      }}
                      onOpenProCheckout={() => setIsProCheckoutOpen(true)}
                      onOpenReferralModal={() => setIsReferralModalOpen(true)}
                    />
                  )}

                  {activeTab === 'warmups' && (
                    <WarmupView
                      user={safeUser}
                      onEarnXp={handleEarnXp}
                      onOpenStatLog={handleOpenStatLog}
                      onNavigateToAnalytics={() => handleTabChange('analytics')}
                    />
                  )}

                  {activeTab === 'feed' && (
                    <SocialFeedView
                      user={safeUser}
                      allAthletes={filteredAthletes}
                      onNavigateTab={handleTabChange}
                      onSendFriendRequest={handleSendFriendRequest}
                      onDirectMessageAthlete={handleDirectMessageAthlete}
                    />
                  )}

                  {activeTab === 'analytics' && (
                    <StatAnalyticsView
                      user={safeUser}
                      athletes={athletes}
                      onOpenProCheckout={() => setIsProCheckoutOpen(true)}
                    />
                  )}

                  {activeTab === 'games' && (
                    <PickupGamesView
                      games={filteredPickupGames}
                      user={safeUser}
                      courts={courts}
                      onOpenHostGame={() => setIsHostGameOpen(true)}
                      onJoinGame={handleJoinGame}
                      onShareGame={(game) => handleShare(game.title)}
                    />
                  )}

                  {activeTab === 'tournaments' && (
                    <TournamentsView
                      tournaments={filteredTournaments}
                      courts={courts}
                      user={safeUser}
                      onUpdateUser={handleUpdateProfile}
                      onCreateTournament={handleCreateTournament}
                      onRegisterTeam={handleRegisterTeam}
                      onUpdateMatchScore={handleUpdateMatchScore}
                      onUpdatePrizePool={handleUpdateTournamentPrizePool}
                      onSelectCourtOnMap={(courtId) => {
                        setActiveTab('map');
                      }}
                    />
                  )}

                  {activeTab === 'map' && (
                    <CourtsTab
                      courts={courts}
                      pickupGames={filteredPickupGames}
                      favoriteCourtIds={safeUser.favoriteCourtIds || []}
                      onToggleFavoriteCourt={handleToggleFavoriteCourt}
                      user={safeUser}
                      athletes={athletes}
                      onSelectAthlete={(ath) => handleDirectMessageAthlete(ath)}
                      onHostAtCourt={(court) => {
                        setSelectedCourtForHost(court);
                        setIsHostGameOpen(true);
                      }}
                      onTriggerGeofenceAlert={handleTriggerGeofenceAlert}
                      onOpenCreatePark={() => setIsCreateParkOpen(true)}
                    />
                  )}

                  {activeTab === 'volleyball_leaderboard' && (
                    <VolleyballLeaderboardView
                      athletes={filteredAthletes}
                      currentUser={safeUser}
                      onShareAthlete={(athlete) => handleShare(athlete.name)}
                    />
                  )}

                  {activeTab === 'leaderboard' && (
                    <LeaderboardView
                      athletes={filteredAthletes}
                      currentUser={safeUser}
                      onShareAthlete={(athlete) => handleShare(athlete.name)}
                    />
                  )}

                  {activeTab === 'scouts' && (
                    <ScoutsView
                      user={safeUser}
                      onUpdateProfile={handleUpdateProfile}
                      showToast={showToast}
                      onOpenProCheckout={() => setIsProCheckoutOpen(true)}
                    />
                  )}

                  {activeTab === 'achievements' && (
                    <Achievements
                      user={safeUser}
                      allAthletes={filteredAthletes}
                      activePalette={activePalette}
                      initialSubTab="showcase"
                      onUpdateProfile={handleUpdateProfile}
                      onClaimXp={(amount, badgeName) => {
                        handleEarnXp(amount, badgeName);
                      }}
                      onOpenUploadModal={handleOpenUploadHighlightModal}
                    />
                  )}

                  {activeTab === 'highlights' && (
                    <Achievements
                      user={safeUser}
                      allAthletes={filteredAthletes}
                      activePalette={activePalette}
                      initialSubTab="reels"
                      onUpdateProfile={handleUpdateProfile}
                      onClaimXp={(amount, badgeName) => {
                        handleEarnXp(amount, badgeName);
                      }}
                      onOpenUploadModal={handleOpenUploadHighlightModal}
                    />
                  )}

                  {activeTab === 'chat' && (
                    <TeamChatView
                      messages={messages}
                      user={safeUser}
                      allAthletes={athletes}
                      onSendMessage={handleSendMessage}
                      onMessagesRead={handleMessagesRead}
                      onSendFriendRequest={handleSendFriendRequest}
                      onJoinGame={handleJoinGame}
                      onNavigateToTab={handleTabChange}
                      directMessageTarget={directMessageTargetAthlete}
                      onDeleteConversation={handleDeleteConversation}
                    />
                  )}

                  {activeTab === 'profile' && (
                    <ProfileView
                      user={safeUser}
                      allAthletes={filteredAthletes}
                      onSelectAthlete={(ath) => {
                        setActiveTab('leaderboard');
                        showToast(`Navigated to Leaderboard to compare with ${ath.name}`);
                      }}
                      onUpdateProfile={(updated) => {
                        handleUpdateProfile(updated);
                        showToast('Profile & preferences updated and saved!');
                      }}
                      isDarkMode={isDarkMode}
                      setIsDarkMode={setIsDarkMode}
                      onOpenAuth={() => setActiveTab('auth')}
                      onOpenUploadHighlight={() => setIsUploadHighlightOpen(true)}
                      onLogout={handleLogout}
                      activePalette={activePalette}
                      onSelectPalette={(paletteId) => {
                        setActivePalette(paletteId);
                        showToast(`Applied "${paletteId.replace('_', ' ').toUpperCase()}" theme palette! 🎨`);
                      }}
                      pendingQueue={pendingQueue}
                      onRetryQueueItem={(id) => {
                        backgroundSync.retryItem(id);
                        showToast(`⚡ Triggered adaptive background retry for queue item.`);
                      }}
                      onClearSyncedQueue={() => {
                        backgroundSync.clearSynced();
                        showToast('Cleared synced historical log entries from local storage queue.');
                      }}
                      onSimulateOfflineAction={handleSimulateOfflineAction}
                      onNavigateToTab={handleTabChange}
                      onOpenStatLog={handleOpenStatLog}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </React.Suspense>
        </main>
      </div>

      {/* Modals & Drawers */}
      <StatLogModal
        isOpen={isStatLogOpen}
        onClose={() => {
          setIsStatLogOpen(false);
          setStatLogPrefilledData(null);
        }}
        onSubmitStats={handleLogStats}
        user={user}
        prefilledData={statLogPrefilledData}
        activeSportFilter={activeSportFilter}
      />

      <HostGameModal
        isOpen={isHostGameOpen}
        onClose={() => setIsHostGameOpen(false)}
        courts={courts}
        initialCourt={selectedCourtForHost}
        onHostGame={handleHostGame}
      />

      <CreateParkModal
        isOpen={isCreateParkOpen}
        onClose={() => setIsCreateParkOpen(false)}
        user={user}
        onAddCourt={handleAddCourt}
      />

      <NotificationDrawer
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        notifications={notifications}
        onMarkAllRead={() => {
          const updated = notifications.map((n) => ({ ...n, read: true }));
          setNotifications(updated);
          updated.forEach((n) => saveNotificationToFirestore(n));
          showToast('All notifications marked as read ✓');
        }}
        onClearAllNotifications={() => {
          const nowMs = Date.now();
          const ids = notifications.map((n) => n.id);

          // 1. Persist lastClearedTimestamp and clearedNotificationIds in user profile in Firestore and localStorage
          setUser((prev) => {
            const updatedUser: AthleteProfile = {
              ...prev,
              lastClearedTimestamp: nowMs,
              clearedNotificationIds: Array.from(new Set([...(prev.clearedNotificationIds || []), ...ids])),
            };
            safeStoreUserAndFirestore(updatedUser);
            return updatedUser;
          });

          // 2. Persist cleared status in localStorage fallback
          try {
            const raw = localStorage.getItem('playground_cleared_notif_ids');
            const set = raw ? new Set(JSON.parse(raw)) : new Set<string>();
            ids.forEach((id) => set.add(id));
            localStorage.setItem('playground_cleared_notif_ids', JSON.stringify(Array.from(set)));
          } catch (e) { }

          // 3. Update Firestore notification documents with cleared: true and delete from collection
          notifications.forEach((n) => {
            saveNotificationToFirestore({ ...n, cleared: true });
          });
          clearAllNotificationsFromFirestore();

          // 4. Update local state
          setNotifications([]);
          showToast('Cleared all push & geo notifications!');
        }}
        onDismissNotification={(notifId) => {
          // 1. Persist clearedNotificationIds in user profile in Firestore and localStorage
          setUser((prev) => {
            const updatedUser: AthleteProfile = {
              ...prev,
              clearedNotificationIds: Array.from(new Set([...(prev.clearedNotificationIds || []), notifId])),
            };
            safeStoreUserAndFirestore(updatedUser);
            return updatedUser;
          });

          // 2. Persist cleared status in localStorage fallback
          try {
            const raw = localStorage.getItem('playground_cleared_notif_ids');
            const set = raw ? new Set(JSON.parse(raw)) : new Set<string>();
            set.add(notifId);
            localStorage.setItem('playground_cleared_notif_ids', JSON.stringify(Array.from(set)));
          } catch (e) { }

          // 3. Mark notification document as cleared in Firestore and delete
          const targetNotif = notifications.find((n) => n.id === notifId);
          if (targetNotif) {
            saveNotificationToFirestore({ ...targetNotif, cleared: true });
          }
          deleteNotificationFromFirestore(notifId);

          // 4. Update local state
          setNotifications((prev) => prev.filter((n) => n.id !== notifId));
          showToast('Notification cleared.');
        }}
        onAcceptFriendRequest={handleAcceptFriendRequest}
        onDeclineFriendRequest={handleDeclineFriendRequest}
      />

      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        user={user}
        isAuthInitializing={isAuthInitializing}
        authProviderSettings={authProviderSettings}
        onOpenSignUp={() => setIsSignUpOpen(true)}
        onLoginSuccess={handleLoginSuccess}
        onSelectDemoAccount={handleSelectDemoAccount}
      />

      <SignUpModal
        isOpen={isSignUpOpen}
        onClose={() => setIsSignUpOpen(false)}
        isAuthInitializing={isAuthInitializing}
        onRegisterUser={handleRegisterUser}
        onSwitchToLogin={() => setIsAuthOpen(true)}
      />

      <UploadHighlightModal
        isOpen={isUploadHighlightOpen}
        onClose={() => {
          setIsUploadHighlightOpen(false);
          setHighlightPresetTitle(undefined);
          setHighlightPresetSport(undefined);
        }}
        onAddHighlight={handleAddHighlight}
        presetTitle={highlightPresetTitle}
        presetSport={highlightPresetSport}
      />

      <MiniGamesModal
        isOpen={isMiniGamesOpen}
        onClose={() => setIsMiniGamesOpen(false)}
        user={user}
        onEarnXpAndStats={handleMiniGamesEarnXpAndStats}
      />

      <ProCheckoutModal
        isOpen={isProCheckoutOpen}
        onClose={() => setIsProCheckoutOpen(false)}
        user={user}
        onPaymentSuccess={(updatedPartial) => {
          handleUpdateProfile(updatedPartial);
          handleEarnXp(250, 'Upgraded to PRO Membership Tier 👑');
          setIsProCheckoutOpen(false);
          showToast('🎉 PRO Membership payment verified and activated!');
        }}
      />

      {/* STRIPE INTERSTITIAL STATUS TRACKER */}
      {activeStripeTrackingSessionId && (
        <StripePaymentStatusTracker
          sessionId={activeStripeTrackingSessionId}
          userId={user?.id || 'current_athlete'}
          currentUser={user}
          onRetryCheckout={() => {
            setActiveStripeTrackingSessionId(null);
            setIsProCheckoutOpen(true);
          }}
          onVerified={(res) => {
            setActiveStripeTrackingSessionId(null);
            try {
              sessionStorage.removeItem('pending_stripe_session_id');
              window.history.replaceState({}, '', window.location.pathname);
            } catch (e) { }
            handleUpdateProfile({
              isPro: true,
              isVerifiedPro: true,
              subscriptionTier: 'pro',
              paymentReceiptId: res.paymentReceiptId,
              paymentMethod: 'stripe',
              paymentDate: new Date().toISOString(),
              role: user?.role === 'admin' ? 'admin' : 'playground_pro',
              ...res,
            });
            handleEarnXp(250, 'Upgraded to PRO Membership Tier 👑');
            triggerConfetti();
            showToast('👑 Welcome to Playground PRO! Your Gold Verified Badge is now active.');
          }}
          onClose={() => {
            setActiveStripeTrackingSessionId(null);
            try {
              sessionStorage.removeItem('pending_stripe_session_id');
              window.history.replaceState({}, '', window.location.pathname);
            } catch (e) { }
          }}
        />
      )}

      <VerificationGatedModal
        isOpen={isVerificationGatedOpen}
        onClose={() => setIsVerificationGatedOpen(false)}
        userEmail={safeUser.email}
        featureName={gatedFeatureName}
        onVerifyNow={() => {
          handleUpdateProfile({ emailVerified: true });
          setIsVerificationGatedOpen(false);
          setActiveTab('dashboard');
          showToast('Email verified! Feature unlocked. 🎉');
        }}
      />

      {/* ONE-TIME LOCATION ONBOARDING MODAL AFTER REGISTRATION */}
      <LocationOnboardingModal
        isOpen={Boolean(safeUser && safeUser.hasCompletedLocationOnboarding === false)}
        user={safeUser}
        onConfirmLocation={(state, city) => {
          handleUpdateProfile({
            registeredState: state,
            registeredCity: city,
            hasCompletedLocationOnboarding: true,
            location: {
              ...(safeUser.location || { address: `${city}, ${state}`, lat: 40.7128, lng: -74.006 }),
              city: city,
              state: state,
            },
          });
          showToast(`📍 Regional location confirmed as ${city}, ${state}! Court map & leaderboards updated.`);
        }}
      />

      {/* LEGENDARY 1,000+ CAREER POINTS THRESHOLD MODAL NOTIFICATION */}
      {legendaryModal?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fadeIn">
          <div className="bg-gradient-to-br from-amber-950 via-indigo-950 to-indigo-900 p-6 sm:p-8 rounded-[2.5rem] border-2 border-amber-300 shadow-[0_0_60px_rgba(251,191,36,0.7)] max-w-lg w-full space-y-6 relative overflow-hidden">
            <button
              type="button"
              onClick={() => setLegendaryModal(null)}
              className="absolute top-5 right-5 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full border border-white/20 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center space-y-2">
              <div className="inline-flex items-center space-x-2 px-3.5 py-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black text-xs font-black italic uppercase rounded-full font-mono shadow-xl animate-bounce">
                <Crown className="w-4 h-4 fill-black" />
                <span>ELITE SERIES LEGENDARY THRESHOLD 👑</span>
              </div>
              <h2 className="text-3xl font-black italic uppercase text-white tracking-tight">
                {legendaryModal.title}
              </h2>
              <p className="text-xs text-amber-300 font-bold font-mono">
                1,000+ Career Points Milestone • Verified Scout Audit
              </p>
            </div>

            <div className="bg-indigo-950/80 p-5 rounded-3xl border border-amber-400/50 space-y-3 text-center relative">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-400/20 text-amber-300 border border-amber-400/60 flex items-center justify-center shadow-inner">
                <Crown className="w-9 h-9 stroke-[2.5]" />
              </div>
              <div>
                <h3 className="text-lg font-black italic uppercase text-white">
                  {legendaryModal.badgeName}
                </h3>
                <p className="text-xs text-indigo-200/90 italic leading-relaxed mt-1">
                  {legendaryModal.description}
                </p>
              </div>
              <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs font-mono">
                <span className="text-indigo-300 uppercase">Career Score Logged</span>
                <span className="font-black text-amber-300 text-sm">
                  {legendaryModal.currentPoints.toLocaleString()} PTS
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => {
                  fireEliteSeriesConfetti();
                  setLegendaryModal(null);
                  showToast('👑 Legendary Career Milestone Highlight Saved to Profile!');
                }}
                className="w-full py-4 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-amber-400/30 transition text-center flex items-center justify-center space-x-2 scale-105"
              >
                <Sparkles className="w-4 h-4 fill-black" />
                <span>Claim +{legendaryModal.xpReward.toLocaleString()} XP & Blast Elite Confetti</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referral Rewards Modal */}
      <ReferralModal
        user={safeUser}
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
        onEarnXp={handleEarnXp}
      />

      {/* Level Up Motion Confetti Overlay */}
      <ConfettiExplosion isVisible={showConfetti} />
    </div>
  );
}
